/**
 * InfoTab.jsx — Per-user personalized information and onboarding page
 * Shows: mission status, account info, P&L, strategy performance, live account setup, platform guide
 */
import { useState, useEffect, useCallback, useRef } from "react";
import { formatJaTime } from "./jamaicaTime";

const C = {
  bg:      "#0a0a0f",
  panel:   "#12121a",
  card:    "#1a1a26",
  border:  "#2a2a3a",
  amber:   "#f5a623",
  amberD:  "#c47d0e",
  green:   "#22c55e",
  red:     "#ef4444",
  blue:    "#3b82f6",
  purple:  "#a855f7",
  cyan:    "#06b6d4",
  text:    "#e8e8f0",
  muted:   "#888899",
  dim:     "#555566",
};

const MISSION_END = null;  // Ongoing mission (Rule #9, revised 2026-08-31) — no end date; daily KPI gate instead.
const APPROVED_STRATEGIES = ["90_STRATEGY.md", "KARA_APEX_ASYMMETRIC_350.md", "JD_NIGHT_OWL_URUS.md", "LIVE_EDGE_BASKET.md"];

function Badge({ color, children, style = {} }) {
  return (
    <span style={{
      background: color + "22", color, border: `1px solid ${color}44`,
      borderRadius: 6, padding: "2px 10px", fontSize: 12, fontWeight: 700,
      display: "inline-block",
      ...style
    }}>
      {children}
    </span>
  );
}

function Card({ title, icon, children, style = {}, accent }) {
  return (
    <div style={{
      background: C.card,
      border: `1px solid ${accent || C.border}`,
      borderRadius: 12,
      padding: "20px 24px",
      marginBottom: 20,
      ...style
    }}>
      {title && (
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
          {icon && <span style={{ fontSize: 20 }}>{icon}</span>}
          <h3 style={{ margin: 0, color: accent || C.amber, fontSize: 15, letterSpacing: 0.5 }}>{title}</h3>
        </div>
      )}
      {children}
    </div>
  );
}

function Step({ n, title, children }) {
  return (
    <div style={{ display: "flex", gap: 14, marginBottom: 16 }}>
      <div style={{
        width: 28, height: 28, borderRadius: "50%", background: C.amber, color: "#000",
        fontWeight: 800, fontSize: 13, display: "flex", alignItems: "center", justifyContent: "center",
        flexShrink: 0, marginTop: 2,
      }}>{n}</div>
      <div>
        <div style={{ color: C.text, fontWeight: 700, marginBottom: 4, fontSize: 14 }}>{title}</div>
        <div style={{ color: C.muted, fontSize: 13, lineHeight: 1.6 }}>{children}</div>
      </div>
    </div>
  );
}

function StatBox({ label, value, sub, color = C.text }) {
  return (
    <div style={{
      background: C.panel, border: `1px solid ${C.border}`, borderRadius: 8,
      padding: "12px 16px", textAlign: "center", flex: 1, minWidth: 100
    }}>
      <div style={{ color: C.muted, fontSize: 11, textTransform: "uppercase", letterSpacing: 1, marginBottom: 4 }}>{label}</div>
      <div style={{ color, fontSize: 22, fontWeight: 800, fontFamily: "monospace" }}>{value}</div>
      {sub && <div style={{ color: C.muted, fontSize: 11, marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

function GateRow({ label, met, value }) {
  return (
    <div style={{
      display: "flex", alignItems: "center", justifyContent: "space-between",
      padding: "5px 0", borderBottom: `1px solid ${C.border}22`,
    }}>
      <span style={{ color: met ? C.green : C.muted, fontSize: 13 }}>
        {met ? "✅" : "⬜"} {label}
      </span>
      {value && <span style={{ color: met ? C.green : C.dim, fontSize: 12, fontFamily: "monospace" }}>{value}</span>}
    </div>
  );
}

const AUTO_REFRESH_SECS = 30;

export default function InfoTab({ accountInfo, token, username, displayName }) {
  const [accountMode, setAccountMode] = useState("demo");
  const [hasLiveToken, setHasLiveToken] = useState(false);
  const [hasDemoToken, setHasDemoToken] = useState(false);
  const [liveToken, setLiveToken] = useState("");
  const [demoToken, setDemoToken] = useState("");
  const [tokenMsg, setTokenMsg] = useState(null);
  const [switchMsg, setSwitchMsg] = useState(null);
  const [loading, setLoading] = useState(false);
  const [switchLoading, setSwitchLoading] = useState(false);
  const [strategyStatus, setStrategyStatus] = useState(null);
  const [missionStatus, setMissionStatus] = useState(null);
  const [tradeStats, setTradeStats] = useState(null);
  const [bestHours, setBestHours] = useState(null);
  const [strategyTimeline, setStrategyTimeline] = useState(null);
  const [recentTrades, setRecentTrades] = useState([]);
  const [sessionStats, setSessionStats] = useState(null);
  const [symbolWr, setSymbolWr] = useState(null);
  const [scannerStatus, setScannerStatus] = useState(null);
  const [refreshCountdown, setRefreshCountdown] = useState(AUTO_REFRESH_SECS);
  const [lastRefreshed, setLastRefreshed] = useState(null);
  const [section, setSection] = useState("mission");
  const [autoOptLoading, setAutoOptLoading] = useState(false);
  const [autoOptResult, setAutoOptResult] = useState(null);
  const [autoOptError, setAutoOptError] = useState(null);
  const refreshTimer = useRef(null);
  const countdownTimer = useRef(null);

  const authHeaders = { "Content-Type": "application/json", "Authorization": `Bearer ${token}` };

  const fetchAll = useCallback(async () => {
    try {
      const activeStrategy = strategyStatus?.active_by_user?.[username] || "JD_NIGHT_OWL_URUS.md";  // Default per Rule #9 (ken72: JD_NIGHT_OWL_URUS, lando: LIVE_EDGE_BASKET)
      const [modeR, stratR, missionR, hoursR, timelineR, tradesR, sessionsR, symWrR, scannerR] = await Promise.allSettled([
        fetch("/api/account/mode", { headers: authHeaders }),
        fetch("/api/strategies/list", { headers: authHeaders }),
        fetch("/api/mission/status", { headers: authHeaders }),
        fetch("/api/analytics/best-hours?min_trades=5", { headers: authHeaders }),
        fetch("/api/analytics/strategy-timeline", { headers: authHeaders }),
        fetch("/api/trades/history?limit=20", { headers: authHeaders }),
        fetch("/api/analytics/sessions", { headers: authHeaders }),
        fetch(`/api/analytics/symbol-wr?strategy=${encodeURIComponent(activeStrategy)}&min_trades=3`, { headers: authHeaders }),
        fetch("/api/scanner/status", { headers: authHeaders }),
      ]);
      if (modeR.status === "fulfilled" && modeR.value.ok) {
        const d = await modeR.value.json();
        setAccountMode(d.account_mode || "demo");
        setHasLiveToken(!!d.has_live_token);
        setHasDemoToken(!!d.has_demo_token);
      }
      if (stratR.status === "fulfilled" && stratR.value.ok) setStrategyStatus(await stratR.value.json());
      if (missionR.status === "fulfilled" && missionR.value.ok) setMissionStatus(await missionR.value.json());
      if (hoursR.status === "fulfilled" && hoursR.value.ok) setBestHours(await hoursR.value.json());
      if (timelineR.status === "fulfilled" && timelineR.value.ok) setStrategyTimeline(await timelineR.value.json());
      if (tradesR.status === "fulfilled" && tradesR.value.ok) {
        const trades = await tradesR.value.json();
        setRecentTrades(Array.isArray(trades) ? trades.slice(0, 20).reverse() : []);
      }
      if (sessionsR.status === "fulfilled" && sessionsR.value.ok) {
        const d = await sessionsR.value.json();
        setSessionStats(d.sessions || d);
      }
      if (symWrR.status === "fulfilled" && symWrR.value.ok) {
        const d = await symWrR.value.json();
        setSymbolWr(d.symbol_wr || null);
      }
      if (scannerR.status === "fulfilled" && scannerR.value.ok) {
        const d = await scannerR.value.json();
        setScannerStatus(d);
      }
      setLastRefreshed(new Date());
      setRefreshCountdown(AUTO_REFRESH_SECS);
    } catch {}
  }, [token]);

  // Initial fetch + auto-refresh every 30s
  useEffect(() => {
    fetchAll();
    refreshTimer.current = setInterval(fetchAll, AUTO_REFRESH_SECS * 1000);
    return () => clearInterval(refreshTimer.current);
  }, [fetchAll]);

  // Countdown ticker
  useEffect(() => {
    countdownTimer.current = setInterval(() => {
      setRefreshCountdown(c => Math.max(0, c - 1));
    }, 1000);
    return () => clearInterval(countdownTimer.current);
  }, []);

  useEffect(() => {
    if (!accountInfo) return;
    setTradeStats({
      daily:   accountInfo.dailyPnl    ?? 0,
      weekly:  accountInfo.weeklyPnl   ?? 0,
      monthly: accountInfo.monthlyPnl  ?? 0,
      total:   accountInfo.totalProfit ?? 0,
      wrDaily:  (accountInfo.winRates?.daily  ?? 0) / 100,
      wrWeekly: (accountInfo.winRates?.weekly ?? 0) / 100,
      wrAll:    (accountInfo.winRates?.overall ?? 0) / 100,
      dailyW:   accountInfo.winRates?.dailyWins    ?? 0,
      dailyT:   accountInfo.winRates?.dailyTotal   ?? 0,
      weeklyW:  accountInfo.winRates?.weeklyWins   ?? 0,
      weeklyT:  accountInfo.winRates?.weeklyTotal  ?? 0,
      monthlyW: accountInfo.winRates?.monthlyWins  ?? 0,
      monthlyT: accountInfo.winRates?.monthlyTotal ?? 0,
      allW:     accountInfo.winRates?.overallWins  ?? 0,
      allT:     accountInfo.winRates?.overallTotal ?? 0,
    });
  }, [accountInfo]);

  const handleSetLiveToken = async () => {
    if (!liveToken.trim()) return;
    setLoading(true); setTokenMsg(null);
    try {
      const r = await fetch("/api/account/set-live-token", {
        method: "POST", headers: authHeaders,
        body: JSON.stringify({ token: liveToken.trim() }),
      });
      const d = await r.json();
      if (r.ok) {
        setTokenMsg({ type: "success", text: `✅ ${d.message} (${d.loginid}, ${d.currency})` });
        setLiveToken(""); fetchAll();
      } else {
        setTokenMsg({ type: "error", text: `❌ ${d.error}` });
      }
    } catch (e) {
      setTokenMsg({ type: "error", text: `❌ ${e.message}` });
    } finally { setLoading(false); }
  };

  const handleSetDemoToken = async () => {
    if (!demoToken.trim()) return;
    setLoading(true); setTokenMsg(null);
    try {
      const r = await fetch("/api/account/set-demo-token", {
        method: "POST", headers: authHeaders,
        body: JSON.stringify({ token: demoToken.trim() }),
      });
      const d = await r.json();
      if (r.ok) {
        setTokenMsg({ type: "success", text: `✅ ${d.message} (${d.loginid}, ${d.currency})` });
        setDemoToken(""); fetchAll();
      } else {
        setTokenMsg({ type: "error", text: `❌ ${d.error}` });
      }
    } catch (e) {
      setTokenMsg({ type: "error", text: `❌ ${e.message}` });
    } finally { setLoading(false); }
  };

  const handleSwitchMode = async (targetMode) => {
    if (targetMode === accountMode) return;
    if (targetMode === "live") {
      const certified = APPROVED_STRATEGIES.filter(n => strategyStatus?.strategies?.[n]?.forge_certified).length;
      if (certified < 2) {
        setSwitchMsg({
          type: "error",
          text: `⛔ Live trading is locked until at least 2 of 3 strategies are forge-certified (mission target: June 19, 2026). Currently ${certified}/3 certified.`,
        });
        return;
      }
      if (!window.confirm(
        "⚠️ LIVE TRADING MODE\n\n" +
        "REAL MONEY will be at risk. All trades will execute on your real Deriv account.\n\n" +
        "• Mission complete: 2+ strategies forge-certified ✅\n" +
        "• Start with a small stake ($5–$10)\n" +
        "• Monitor the first 20 live trades closely\n\n" +
        "Click OK to confirm switching to LIVE mode."
      )) return;
    }
    setSwitchLoading(true); setSwitchMsg(null);
    try {
      const r = await fetch("/api/account/switch-mode", {
        method: "POST", headers: authHeaders,
        body: JSON.stringify({ mode: targetMode }),
      });
      const d = await r.json();
      if (r.ok) {
        setAccountMode(targetMode);
        setSwitchMsg({ type: "success", text: `✅ ${d.message}` });
      } else {
        setSwitchMsg({ type: "error", text: `❌ ${d.error}` });
      }
    } catch (e) {
      setSwitchMsg({ type: "error", text: `❌ ${e.message}` });
    } finally { setSwitchLoading(false); }
  };

  const pnlColor = (v) => v > 0 ? C.green : v < 0 ? C.red : C.muted;
  const fmtPnl = (v) => `${v >= 0 ? "+" : ""}${Number(v).toFixed(2)}`;
  const fmtWr = (v) => `${(v * 100).toFixed(1)}%`;
  const fmtPct = (v) => v != null ? `${(v * 100).toFixed(1)}%` : "—";

  const navStyle = (id) => ({
    padding: "8px 18px", borderRadius: 6, cursor: "pointer", fontSize: 13,
    fontWeight: 700, border: "none", letterSpacing: 0.5,
    background: section === id ? C.amber : "transparent",
    color: section === id ? "#000" : C.muted,
    transition: "all 0.15s",
  });

  const activeStrategy = strategyStatus?.active_by_user?.[username];
  const activeSt = activeStrategy ? strategyStatus?.strategies?.[activeStrategy] : null;

  const certifiedCount = APPROVED_STRATEGIES.filter(n => strategyStatus?.strategies?.[n]?.forge_certified).length;
  const missionComplete = certifiedCount >= Math.ceil(APPROVED_STRATEGIES.length / 2);  // ≥50% of approved certified → both ken72 + lando covered
  const now = new Date();
  const daysLeft = MISSION_END ? Math.max(0, Math.ceil((MISSION_END - now) / 86400000)) : null;  // null = ongoing, no countdown
  const missionProgress = Math.min(100, Math.round((certifiedCount / APPROVED_STRATEGIES.length) * 100));

  // Live mode unlock conditions
  const liveUnlocked = missionComplete && hasLiveToken;

  return (
    <div style={{ padding: "0 8px", maxWidth: 960, margin: "0 auto" }}>

      {/* ── Header ── */}
      <div style={{
        background: `linear-gradient(135deg, ${C.card} 0%, #1a1028 100%)`,
        border: `1px solid ${C.amber}33`, borderRadius: 14, padding: "20px 28px",
        marginBottom: 20, display: "flex", alignItems: "center", justifyContent: "space-between",
        flexWrap: "wrap", gap: 12,
      }}>
        <div>
          <div style={{ color: C.amber, fontSize: 22, fontWeight: 800 }}>
            👤 {displayName || username}'s Dashboard
          </div>
          <div style={{ color: C.muted, fontSize: 13, marginTop: 4 }}>
            {accountInfo?.loginid
              ? <>Account: <b style={{ color: C.text }}>{accountInfo.loginid}</b></>
              : "No Deriv account connected"}
            {accountInfo?.currency ? ` · ${accountInfo.currency}` : ""}
            {accountInfo?.fullname ? ` · ${accountInfo.fullname}` : ""}
            {accountInfo?.is_virtual !== undefined && (
              <span style={{ marginLeft: 8 }}>
                <Badge color={accountInfo.is_virtual ? C.blue : C.green}>
                  {accountInfo.is_virtual ? "DEMO" : "REAL"}
                </Badge>
              </span>
            )}
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
          <div style={{ textAlign: "right" }}>
            <div style={{ color: C.muted, fontSize: 11, textTransform: "uppercase" }}>Balance</div>
            <div style={{ color: C.amber, fontSize: 20, fontWeight: 800, fontFamily: "monospace" }}>
              {accountInfo?.balance !== undefined ? `$${accountInfo.balance.toFixed(2)}` : "—"}
            </div>
          </div>
          <div style={{
            padding: "6px 14px", borderRadius: 8,
            background: accountMode === "live" ? C.red + "22" : C.blue + "22",
            border: `2px solid ${accountMode === "live" ? C.red : C.blue}`,
            color: accountMode === "live" ? C.red : C.blue,
            fontWeight: 800, fontSize: 13,
          }}>
            {accountMode === "live" ? "🔴 LIVE MODE" : "🔵 DEMO MODE"}
          </div>
        </div>
      </div>

      {/* ── Mission Alert Banner ── */}
      {missionComplete && (
        <div style={{
          background: `linear-gradient(135deg, ${C.green}18, ${C.green}08)`,
          border: `2px solid ${C.green}55`, borderRadius: 12, padding: "14px 20px",
          marginBottom: 20, display: "flex", alignItems: "center", gap: 14,
        }}>
          <span style={{ fontSize: 32 }}>🏆</span>
          <div>
            <div style={{ color: C.green, fontWeight: 800, fontSize: 16 }}>
              MISSION MILESTONE ACHIEVED — {certifiedCount}/{APPROVED_STRATEGIES.length} Strategies Certified
            </div>
            <div style={{ color: C.muted, fontSize: 13, marginTop: 2 }}>
              {certifiedCount >= 2
                ? `${certifiedCount}+ strategies forge-certified. Live trading mode is now unlocked. Set your real account API token below to begin.`
                : `All ${APPROVED_STRATEGIES.length} approved strategies are forge-certified and ready for live trading.`}
            </div>
          </div>
        </div>
      )}

      {/* ── Auto-refresh status bar ── */}
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        background: C.panel, border: `1px solid ${C.border}`, borderRadius: 8,
        padding: "6px 14px", marginBottom: 12, flexWrap: "wrap", gap: 8,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ width: 8, height: 8, borderRadius: "50%", background: C.green, boxShadow: `0 0 6px ${C.green}` }} />
          <span style={{ color: C.muted, fontSize: 12 }}>Live data</span>
          {lastRefreshed && (
            <span style={{ color: C.dim, fontSize: 11 }}>
              Updated {formatJaTime(lastRefreshed)}
            </span>
          )}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ position: "relative", width: 80, height: 4, background: C.border, borderRadius: 4, overflow: "hidden" }}>
            <div style={{
              position: "absolute", left: 0, top: 0, height: "100%", borderRadius: 4,
              width: `${(refreshCountdown / AUTO_REFRESH_SECS) * 100}%`,
              background: refreshCountdown > 10 ? C.green : C.amber,
              transition: "width 0.9s linear",
            }} />
          </div>
          <span style={{ color: C.dim, fontSize: 11 }}>{refreshCountdown}s</span>
          <button
            onClick={() => { fetchAll(); }}
            style={{
              background: "transparent", border: `1px solid ${C.border}`, borderRadius: 5,
              color: C.muted, fontSize: 11, padding: "2px 8px", cursor: "pointer",
            }}
          >↻ Refresh</button>
        </div>
      </div>

      {/* ── Sub-nav ── */}
      <div style={{ display: "flex", gap: 6, marginBottom: 20, flexWrap: "wrap" }}>
        <button style={navStyle("mission")} onClick={() => setSection("mission")}>🎯 Mission</button>
        <button style={navStyle("performance")} onClick={() => setSection("performance")}>📊 Performance</button>
        <button style={navStyle("trades")} onClick={() => setSection("trades")}>📋 Trades</button>
        <button style={navStyle("scanner")} onClick={() => setSection("scanner")}>📡 Scanner</button>
        <button style={navStyle("account")} onClick={() => setSection("account")}>⚙️ Account Setup</button>
        <button style={navStyle("deriv")} onClick={() => setSection("deriv")}>🔑 Deriv API Guide</button>
        <button style={navStyle("guide")} onClick={() => setSection("guide")}>📚 How to Use</button>
        <button style={navStyle("mt5")} onClick={() => setSection("mt5")}>🖥️ MT5 & RDP Setup</button>
      </div>

      {/* ════════════ MISSION TAB ════════════ */}
      {section === "mission" && (
        <>
          {/* Mission Progress */}
          <Card title="Mission: $100/day/user ongoing (since 2026-08-31)" icon="🎯" accent={C.amber}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 10, flexWrap: "wrap", gap: 8 }}>
              <div style={{ color: C.muted, fontSize: 13 }}>
                Daily KPI: ≥ $100 net realised profit per user (Kenyon + Lando = $200/day combined). Auto-deactivate any strategy that drops below 45% WR over 30 settled trades.
              </div>
              <Badge color={daysLeft == null ? C.green : daysLeft > 3 ? C.blue : C.red}>{daysLeft == null ? "∞ ongoing" : `${daysLeft}d remaining`}</Badge>
            </div>
            <div style={{ background: C.panel, borderRadius: 8, height: 10, overflow: "hidden", marginBottom: 8 }}>
              <div style={{
                height: "100%", width: `${missionProgress}%`,
                background: missionProgress >= 100 ? C.green : missionProgress >= 66 ? C.amber : C.blue,
                borderRadius: 8, transition: "width 0.5s",
              }} />
            </div>
            <div style={{ color: C.muted, fontSize: 12, marginBottom: 20 }}>
              {certifiedCount}/{APPROVED_STRATEGIES.length} approved strategies certified ({missionProgress}%)
            </div>

            {/* Per-strategy gates */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 16 }}>
              {APPROVED_STRATEGIES.map((name, i) => {
                const st = strategyStatus?.strategies?.[name];
                const ms = missionStatus?.strategies?.[name];
                const phases = ["Baseline (Jan–Aug 2026)", "Rev1: KARA_APEX_ASYMMETRIC_350 (Sep 2026)", "Rev2: JD_NIGHT_OWL_URUS + LIVE_EDGE_BASKET (Sep 2026)", "Rev3: Rule #9 $100/day (Rev. 2026-08-31, ongoing)"];
                const certified = st?.forge_certified || ms?.forge_certified;
                const liveReady = st?.live_ready;
                const wr = st?.win_rate ?? ms?.win_rate ?? null;
                const tpd = st?.trades_per_day ?? ms?.trades_per_day ?? null;
                const gates = ms?.gates || [];
                return (
                  <div key={name} style={{
                    background: C.panel, border: `2px solid ${certified ? C.green : liveReady ? C.amber : C.border}`,
                    borderRadius: 10, padding: "14px 16px",
                  }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 }}>
                      <div>
                        <div style={{ color: C.text, fontWeight: 700, fontSize: 13 }}>
                          {name.replace(".md", "").replace(/_/g, " ")}
                        </div>
                        <div style={{ color: C.dim, fontSize: 11 }}>{phases[i]}</div>
                      </div>
                      {certified
                        ? <Badge color={C.green}>✅ CERTIFIED</Badge>
                        : liveReady
                          ? <Badge color={C.amber}>LIVE READY</Badge>
                          : <Badge color={C.muted}>PENDING</Badge>
                      }
                    </div>
                    <div style={{ display: "flex", gap: 10, marginBottom: 10 }}>
                      {wr != null && (
                        <div style={{ textAlign: "center" }}>
                          <div style={{ color: C.muted, fontSize: 10 }}>WIN RATE</div>
                          <div style={{ color: wr >= 0.8 ? C.green : wr >= 0.6 ? C.amber : C.red, fontWeight: 700, fontSize: 16, fontFamily: "monospace" }}>
                            {fmtPct(wr)}
                          </div>
                        </div>
                      )}
                      {tpd != null && (
                        <div style={{ textAlign: "center" }}>
                          <div style={{ color: C.muted, fontSize: 10 }}>TPD</div>
                          <div style={{ color: tpd >= 10 ? C.green : tpd >= 2 ? C.amber : C.red, fontWeight: 700, fontSize: 16, fontFamily: "monospace" }}>
                            {Number(tpd).toFixed(1)}
                          </div>
                        </div>
                      )}
                    </div>
                    {gates.length > 0 && (
                      <div>
                        {gates.map(g => <GateRow key={g.key} label={g.label} met={g.met} value={g.value} />)}
                      </div>
                    )}
                    {st?.live_ready_reason && (
                      <div style={{ color: C.muted, fontSize: 11, marginTop: 8, lineHeight: 1.5, borderTop: `1px solid ${C.border}`, paddingTop: 8 }}>
                        {st.live_ready_reason.slice(0, 180)}{st.live_ready_reason.length > 180 ? "…" : ""}
                      </div>
                    )}
                    {st?.allowed_symbols?.length > 0 && (
                      <div style={{ marginTop: 8 }}>
                        <span style={{ color: C.dim, fontSize: 11 }}>Symbols: </span>
                        {st.allowed_symbols.map(s => (
                          <span key={s} style={{ color: C.blue, fontSize: 11, marginRight: 4, fontFamily: "monospace" }}>{s}</span>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </Card>

          {/* Live Account Readiness */}
          <Card
            title="Live Trading Readiness"
            icon="🔴"
            accent={liveUnlocked ? C.green : C.amber}
          >
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 16 }}>
              {[
                { label: "Mission complete (2+ certified)", met: missionComplete },
                { label: "Live API token saved", met: hasLiveToken },
                { label: "Demo trading verified", met: hasDemoToken },
                { label: "Currently in demo mode", met: accountMode === "demo" },
              ].map(({ label, met }) => (
                <div key={label} style={{
                  display: "flex", alignItems: "center", gap: 8,
                  padding: "8px 12px", borderRadius: 8,
                  background: met ? C.green + "10" : C.panel,
                  border: `1px solid ${met ? C.green + "33" : C.border}`,
                }}>
                  <span style={{ fontSize: 16 }}>{met ? "✅" : "⬜"}</span>
                  <span style={{ color: met ? C.green : C.muted, fontSize: 13 }}>{label}</span>
                </div>
              ))}
            </div>
            {liveUnlocked ? (
              <div style={{
                background: C.green + "15", border: `1px solid ${C.green}44`,
                borderRadius: 10, padding: "14px 18px",
              }}>
                <div style={{ color: C.green, fontWeight: 700, fontSize: 15, marginBottom: 6 }}>
                  🟢 LIVE TRADING UNLOCKED
                </div>
                <div style={{ color: C.muted, fontSize: 13, lineHeight: 1.6 }}>
                  All prerequisites met. Go to <b style={{ color: C.amber }}>Account Setup</b> section to switch to Live Mode.
                  Start with your minimum stake and monitor closely for the first 20 trades.
                </div>
              </div>
            ) : (
              <div style={{
                background: C.amber + "10", border: `1px solid ${C.amber}33`,
                borderRadius: 10, padding: "14px 18px",
              }}>
                <div style={{ color: C.amber, fontWeight: 700, fontSize: 14, marginBottom: 6 }}>
                  🔒 Live Mode Locked
                </div>
                <div style={{ color: C.muted, fontSize: 13, lineHeight: 1.6 }}>
                  {!missionComplete
                    ? `Waiting for ${2 - certifiedCount} more strategy certification(s). Target date: June 19, 2026.`
                    : "Add your Deriv real account API token to unlock live trading."}
                </div>
              </div>
            )}
          </Card>

          {/* Current hour status */}
          {missionStatus && (
            <Card title="Current Trading Hour" icon="🕐">
              <div style={{ display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
                <div style={{
                  width: 80, height: 80, borderRadius: "50%",
                  background: missionStatus.hour_is_dead_zone ? C.red + "22" : C.green + "22",
                  border: `3px solid ${missionStatus.hour_is_dead_zone ? C.red : C.green}`,
                  display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
                }}>
                  <div style={{ color: C.muted, fontSize: 10 }}>UTC</div>
                  <div style={{ color: C.text, fontSize: 22, fontWeight: 800 }}>{missionStatus.current_hour_utc}h</div>
                </div>
                <div>
                  <div style={{ color: missionStatus.hour_is_dead_zone ? C.red : C.green, fontWeight: 700, fontSize: 15 }}>
                    {missionStatus.hour_is_dead_zone ? "⛔ Dead Zone — Skip Trades" : "✅ Active Hour — Trade Freely"}
                  </div>
                  <div style={{ color: C.muted, fontSize: 13, marginTop: 4 }}>
                    Phase {missionStatus.phase} focus: {missionStatus.phase_strategy?.replace(".md","").replace(/_/g," ")}
                  </div>
                  {missionStatus.hour_is_dead_zone && (
                    <div style={{ color: C.amber, fontSize: 12, marginTop: 4 }}>
                      Next active hour: {missionStatus.next_active_hour}:00 UTC
                      ({Math.round(missionStatus.mins_to_next_active)}m away)
                    </div>
                  )}
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 3, marginTop: 8 }}>
                    {Array.from({ length: 24 }, (_, h) => {
                      const isActive = missionStatus.active_hours?.includes(h);
                      const isCurrent = h === missionStatus.current_hour_utc;
                      return (
                        <div key={h} style={{
                          width: 28, height: 22, borderRadius: 4, fontSize: 9,
                          display: "flex", alignItems: "center", justifyContent: "center",
                          background: isCurrent ? C.amber : isActive ? C.green + "22" : C.red + "22",
                          border: `1px solid ${isCurrent ? C.amber : isActive ? C.green + "44" : C.red + "44"}`,
                          color: isCurrent ? "#000" : isActive ? C.green : C.red,
                          fontWeight: isCurrent ? 800 : 400,
                        }}>{h}</div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </Card>
          )}
        </>
      )}

      {/* ════════════ PERFORMANCE TAB ════════════ */}
      {section === "performance" && (
        <>
          {/* P&L Summary */}
          {tradeStats && (
            <>
              <Card title="P&L Summary" icon="💰">
                <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                  <StatBox label="Today" value={fmtPnl(tradeStats.daily)} sub="P&L" color={pnlColor(tradeStats.daily)} />
                  <StatBox label="This Week" value={fmtPnl(tradeStats.weekly)} sub="P&L" color={pnlColor(tradeStats.weekly)} />
                  <StatBox label="This Month" value={fmtPnl(tradeStats.monthly)} sub="P&L" color={pnlColor(tradeStats.monthly)} />
                  <StatBox label="Session" value={fmtPnl(tradeStats.total)} sub="Open+Today" color={pnlColor(tradeStats.total)} />
                </div>
              </Card>

              <Card title="Win Rate" icon="🎯">
                <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                  <StatBox
                    label="Today"
                    value={fmtWr(tradeStats.wrDaily)}
                    sub={`${tradeStats.dailyW}/${tradeStats.dailyT} trades`}
                    color={tradeStats.wrDaily >= 0.8 ? C.green : tradeStats.wrDaily >= 0.6 ? C.amber : C.red}
                  />
                  <StatBox
                    label="This Week"
                    value={fmtWr(tradeStats.wrWeekly)}
                    sub={`${tradeStats.weeklyW}/${tradeStats.weeklyT} trades`}
                    color={tradeStats.wrWeekly >= 0.8 ? C.green : tradeStats.wrWeekly >= 0.6 ? C.amber : C.red}
                  />
                  <StatBox
                    label="This Month"
                    value={fmtWr(tradeStats.wrAll)}
                    sub={`${tradeStats.monthlyW}/${tradeStats.monthlyT} trades`}
                    color={tradeStats.wrAll >= 0.8 ? C.green : tradeStats.wrAll >= 0.6 ? C.amber : C.red}
                  />
                  <StatBox
                    label="All Time"
                    value={fmtWr(tradeStats.wrAll)}
                    sub={`${tradeStats.allW}/${tradeStats.allT} trades`}
                    color={tradeStats.wrAll >= 0.8 ? C.green : tradeStats.wrAll >= 0.6 ? C.amber : C.red}
                  />
                </div>
              </Card>
            </>
          )}

          {/* Active strategy */}
          {activeSt && (
            <Card title="Active Strategy" icon="🗂">
              <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap", gap: 16 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ color: C.text, fontWeight: 700, fontSize: 16, marginBottom: 8 }}>
                    {activeStrategy?.replace(".md", "").replace(/_/g, " ")}
                  </div>
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 12 }}>
                    {activeSt.forge_certified && <Badge color={C.green}>FORGE CERTIFIED</Badge>}
                    {activeSt.live_ready && <Badge color={C.amber}>LIVE READY</Badge>}
                    <Badge color={activeSt.win_rate >= 0.8 ? C.green : activeSt.win_rate >= 0.6 ? C.amber : C.red}>
                      WR {fmtPct(activeSt.win_rate)}
                    </Badge>
                    {activeSt.trades_per_day && (
                      <Badge color={activeSt.trades_per_day >= 10 ? C.green : C.amber}>
                        {Number(activeSt.trades_per_day).toFixed(1)} TPD
                      </Badge>
                    )}
                  </div>
                  {activeSt.per_symbol_wr && Object.keys(activeSt.per_symbol_wr).length > 0 && (
                    <div>
                      <div style={{ color: C.muted, fontSize: 12, marginBottom: 6 }}>Per-symbol live WR:</div>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                        {Object.entries(activeSt.per_symbol_wr).map(([sym, wr]) => (
                          <div key={sym} style={{
                            background: C.panel, border: `1px solid ${wr >= 0.8 ? C.green : wr >= 0.6 ? C.amber : C.red}33`,
                            borderRadius: 6, padding: "4px 10px", fontSize: 12,
                          }}>
                            <span style={{ color: C.muted }}>{sym}: </span>
                            <span style={{ color: wr >= 0.8 ? C.green : wr >= 0.6 ? C.amber : C.red, fontWeight: 700 }}>
                              {fmtPct(wr)}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                {activeSt.live_ready && (
                  <div style={{
                    background: C.green + "15", border: `1px solid ${C.green}33`,
                    borderRadius: 10, padding: "14px 20px", textAlign: "center", flexShrink: 0,
                  }}>
                    <div style={{ color: C.green, fontSize: 28, marginBottom: 4 }}>✅</div>
                    <div style={{ color: C.green, fontWeight: 700, fontSize: 12 }}>READY FOR</div>
                    <div style={{ color: C.green, fontWeight: 700, fontSize: 12 }}>LIVE TRADING</div>
                    {activeSt.live_ready_since && (
                      <div style={{ color: C.dim, fontSize: 10, marginTop: 4 }}>
                        Since {activeSt.live_ready_since.slice(0,10)}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </Card>
          )}

          {/* Strategy history table */}
          {strategyStatus?.strategies && (
            <Card title="All Strategies Overview" icon="📈">
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                  <thead>
                    <tr style={{ color: C.muted, textAlign: "left" }}>
                      <th style={{ padding: "6px 10px", borderBottom: `1px solid ${C.border}` }}>Strategy</th>
                      <th style={{ padding: "6px 10px", borderBottom: `1px solid ${C.border}` }}>Status</th>
                      <th style={{ padding: "6px 10px", borderBottom: `1px solid ${C.border}` }}>Win Rate</th>
                      <th style={{ padding: "6px 10px", borderBottom: `1px solid ${C.border}` }}>TPD</th>
                      <th style={{ padding: "6px 10px", borderBottom: `1px solid ${C.border}` }}>Certified</th>
                      <th style={{ padding: "6px 10px", borderBottom: `1px solid ${C.border}` }}>Live Ready</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(strategyStatus.strategies)
                      .filter(([, v]) => (v.eligible_users || []).includes(username) || APPROVED_STRATEGIES.includes(/* name */ Object.keys(strategyStatus.strategies).find(k => strategyStatus.strategies[k] === v)))
                      .sort(([, a], [, b]) => (b.forge_certified ? 1 : 0) - (a.forge_certified ? 1 : 0) || (b.win_rate || 0) - (a.win_rate || 0))
                      .map(([name, st]) => (
                        <tr key={name} style={{
                          borderBottom: `1px solid ${C.border}22`,
                          background: name === activeStrategy ? C.amber + "08" : "transparent",
                        }}>
                          <td style={{ padding: "8px 10px", color: C.text, fontWeight: name === activeStrategy ? 700 : 400 }}>
                            {name === activeStrategy && <span style={{ color: C.amber }}>▶ </span>}
                            {name.replace(".md", "").replace(/_/g," ")}
                          </td>
                          <td style={{ padding: "8px 10px" }}>
                            <Badge color={
                              st.status === "live_ready" ? C.amber :
                              st.forge_certified ? C.green :
                              st.status === "improved" ? C.blue : C.muted
                            }>{st.forge_certified ? "certified" : st.status}</Badge>
                          </td>
                          <td style={{ padding: "8px 10px", color: (st.win_rate||0) >= 0.8 ? C.green : (st.win_rate||0) >= 0.6 ? C.amber : C.red, fontFamily: "monospace" }}>
                            {st.win_rate ? fmtPct(st.win_rate) : "—"}
                          </td>
                          <td style={{ padding: "8px 10px", color: C.text, fontFamily: "monospace" }}>
                            {st.trades_per_day != null ? Number(st.trades_per_day).toFixed(1) : "—"}
                          </td>
                          <td style={{ padding: "8px 10px" }}>
                            {st.forge_certified ? <Badge color={C.green}>YES</Badge> : <Badge color={C.muted}>NO</Badge>}
                          </td>
                          <td style={{ padding: "8px 10px" }}>
                            {st.live_ready ? <Badge color={C.amber}>YES</Badge> : <Badge color={C.muted}>NO</Badge>}
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </Card>
          )}

          {/* Best trading hours */}
          {bestHours?.best && bestHours.best.length > 0 && (
            <Card title="Best Trading Hours — Your Live Data (UTC)" icon="🕐">
              <div style={{ color: C.muted, fontSize: 12, marginBottom: 14 }}>
                Based on your live trade history. Green = high win rate, Red = avoid.
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {Array.from({ length: 24 }, (_, h) => {
                  const hData = (bestHours.best || []).find(x => x.hour === h);
                  const isGood = hData && hData.win_rate >= 0.7 && hData.trades >= 5;
                  const isBad  = hData && hData.win_rate <  0.5 && hData.trades >= 5;
                  return (
                    <div key={h} style={{
                      width: 48, height: 52, borderRadius: 6, display: "flex", flexDirection: "column",
                      alignItems: "center", justifyContent: "center",
                      background: isGood ? C.green + "22" : isBad ? C.red + "22" : C.panel,
                      border: `1px solid ${isGood ? C.green : isBad ? C.red : C.border}`,
                    }}>
                      <div style={{ color: C.muted, fontSize: 10 }}>{h}h</div>
                      {hData && (
                        <>
                          <div style={{ color: isGood ? C.green : isBad ? C.red : C.text, fontSize: 11, fontWeight: 700 }}>
                            {(hData.win_rate * 100).toFixed(0)}%
                          </div>
                          <div style={{ color: C.dim, fontSize: 9 }}>{hData.trades}t</div>
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            </Card>
          )}
        </>
      )}

      {/* ════════════ TRADES TAB ════════════ */}
      {section === "trades" && (
        <>
          {/* Recent Trades Feed */}
          <Card title="Recent Trades" icon="📋" accent={C.amber}>
            {recentTrades.length === 0 ? (
              <div style={{ color: C.muted, fontSize: 13, textAlign: "center", padding: "20px 0" }}>
                No trades yet — start the scanner and enable auto-trade to see trades here.
              </div>
            ) : (
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                  <thead>
                    <tr style={{ borderBottom: `1px solid ${C.border}` }}>
                      {["Time", "Symbol", "Dir", "Stake", "Duration", "Outcome", "P&L"].map(h => (
                        <th key={h} style={{ padding: "6px 10px", color: C.muted, fontWeight: 600, textAlign: "left", whiteSpace: "nowrap" }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {recentTrades.map((t, i) => {
                      const ts = t.timestamp ? new Date(t.timestamp > 1e12 ? t.timestamp : t.timestamp * 1000) : null;
                      const outcome = (t.outcome || "pending").toUpperCase();
                      const pnl = typeof t.pnl === "number" ? t.pnl : (t.profit ?? null);
                      const oc = outcome === "WIN" ? C.green : outcome === "LOSS" ? C.red : C.amber;
                      return (
                        <tr key={i} style={{
                          borderBottom: `1px solid ${C.border}22`,
                          background: i % 2 === 0 ? "transparent" : C.panel + "66",
                        }}>
                          <td style={{ padding: "6px 10px", color: C.dim, whiteSpace: "nowrap" }}>
                            {ts ? formatJaTime(ts) : "—"}
                          </td>
                          <td style={{ padding: "6px 10px", color: C.cyan, fontFamily: "monospace", fontWeight: 700 }}>
                            {t.symbol || "—"}
                          </td>
                          <td style={{ padding: "6px 10px" }}>
                            <span style={{
                              color: t.direction === "CALL" ? C.green : C.red,
                              fontWeight: 700, fontSize: 11,
                            }}>
                              {t.direction === "CALL" ? "▲ CALL" : t.direction === "PUT" ? "▼ PUT" : t.direction || "—"}
                            </span>
                          </td>
                          <td style={{ padding: "6px 10px", color: C.text, fontFamily: "monospace" }}>
                            ${(t.stake || t.amount || 0).toFixed(2)}
                          </td>
                          <td style={{ padding: "6px 10px", color: C.muted }}>
                            {t.duration ? `${t.duration}m` : "—"}
                          </td>
                          <td style={{ padding: "6px 10px" }}>
                            <span style={{
                              background: oc + "22", color: oc,
                              border: `1px solid ${oc}44`,
                              borderRadius: 5, padding: "2px 8px", fontWeight: 700, fontSize: 11,
                            }}>
                              {outcome === "PENDING" ? "⏳" : outcome === "WIN" ? "✅" : "❌"} {outcome}
                            </span>
                          </td>
                          <td style={{ padding: "6px 10px", fontFamily: "monospace", fontWeight: 700,
                            color: pnl == null ? C.muted : pnl > 0 ? C.green : pnl < 0 ? C.red : C.muted }}>
                            {pnl == null ? "—" : `${pnl >= 0 ? "+" : ""}${pnl.toFixed(2)}`}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </Card>

          {/* Trade Stats Summary */}
          {recentTrades.length > 0 && (() => {
            const settled = recentTrades.filter(t => ["WIN","LOSS","win","loss"].includes((t.outcome||"").toLowerCase()));
            const wins = settled.filter(t => (t.outcome||"").toUpperCase() === "WIN").length;
            const totalPnl = recentTrades.reduce((s, t) => s + (typeof t.pnl === "number" ? t.pnl : (t.profit ?? 0)), 0);
            const wr = settled.length > 0 ? wins / settled.length : null;
            return (
              <Card title="Last 20 Trades Summary" icon="📊">
                <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                  <StatBox label="Trades" value={recentTrades.length} sub="shown" />
                  <StatBox label="Settled" value={settled.length} sub="wins+losses" />
                  <StatBox
                    label="Win Rate"
                    value={wr != null ? `${(wr * 100).toFixed(0)}%` : "—"}
                    sub={`${wins}/${settled.length}`}
                    color={wr != null ? (wr >= 0.8 ? C.green : wr >= 0.6 ? C.amber : C.red) : C.muted}
                  />
                  <StatBox
                    label="P&L"
                    value={`${totalPnl >= 0 ? "+" : ""}$${totalPnl.toFixed(2)}`}
                    sub="last 20"
                    color={pnlColor(totalPnl)}
                  />
                  <StatBox
                    label="Pending"
                    value={recentTrades.filter(t => (t.outcome||"pending") === "pending").length}
                    sub="open"
                    color={C.amber}
                  />
                </div>
              </Card>
            );
          })()}
        </>
      )}

      {/* ════════════ SCANNER TAB ════════════ */}
      {section === "scanner" && (
        <>
          {/* Scanner Connection Status */}
          {scannerStatus && (() => {
            const mySession = Array.isArray(scannerStatus)
              ? scannerStatus.find(s => s.username === username)
              : scannerStatus.username === username ? scannerStatus : null;
            const sessions = Array.isArray(scannerStatus) ? scannerStatus : [scannerStatus].filter(Boolean);
            return (
              <>
                <Card title="Scanner Status" icon="📡" accent={C.cyan}>
                  {sessions.map(s => {
                    const connected = s.wsStatus === "connected" || s.status === "connected";
                    const syms = s.activeSymbols || s.symbols || [];
                    const lastAnalysis = s.lastAnalysis || {};
                    return (
                      <div key={s.username}>
                        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14, flexWrap: "wrap" }}>
                          <span style={{ color: C.text, fontWeight: 700, fontSize: 15 }}>
                            {s.username === username ? "🔑 " : ""}{s.username}
                          </span>
                          <span style={{
                            background: (connected ? C.green : C.red) + "22",
                            color: connected ? C.green : C.red,
                            border: `1px solid ${(connected ? C.green : C.red)}44`,
                            borderRadius: 5, padding: "2px 10px", fontSize: 12, fontWeight: 700,
                          }}>
                            {connected ? "● LIVE" : "○ OFFLINE"}
                          </span>
                          {s.strategyName && (
                            <span style={{ color: C.muted, fontSize: 12 }}>
                              {s.strategyName.replace(".md","").replace(/_/g," ")}
                            </span>
                          )}
                          {s.reconnectAttempts > 0 && (
                            <span style={{ color: C.amber, fontSize: 12 }}>
                              ↻ Reconnect: {s.reconnectAttempts}×
                            </span>
                          )}
                          {s.openTradesCount > 0 && (
                            <span style={{ color: C.purple, fontSize: 12, fontWeight: 700 }}>
                              {s.openTradesCount} open trade{s.openTradesCount !== 1 ? "s" : ""}
                            </span>
                          )}
                        </div>
                        {/* Live price tickers per symbol */}
                        {syms.length > 0 && (
                          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(120px, 1fr))", gap: 8 }}>
                            {syms.map(sym => {
                              const la = lastAnalysis[sym];
                              const dir = la?.direction;
                              const dirColor = dir === "BULL" ? C.green : dir === "BEAR" ? C.red : C.muted;
                              const secsSince = la?.ts ? Math.round((Date.now() - la.ts) / 1000) : null;
                              return (
                                <div key={sym} style={{
                                  background: C.panel, border: `1px solid ${connected ? C.cyan + "33" : C.border}`,
                                  borderRadius: 8, padding: "10px 12px",
                                }}>
                                  <div style={{ color: C.cyan, fontFamily: "monospace", fontWeight: 800, fontSize: 12, marginBottom: 4 }}>
                                    {sym}
                                  </div>
                                  {la?.price ? (
                                    <div style={{ color: C.text, fontFamily: "monospace", fontSize: 13, fontWeight: 700 }}>
                                      {Number(la.price).toPrecision(6)}
                                    </div>
                                  ) : (
                                    <div style={{ color: C.dim, fontSize: 11 }}>loading…</div>
                                  )}
                                  {dir && (
                                    <div style={{ color: dirColor, fontSize: 11, fontWeight: 700, marginTop: 2 }}>
                                      {dir === "BULL" ? "▲" : "▼"} {dir}
                                    </div>
                                  )}
                                  {secsSince != null && (
                                    <div style={{ color: secsSince < 10 ? C.green : C.dim, fontSize: 10, marginTop: 2 }}>
                                      {secsSince < 60 ? `${secsSince}s ago` : `${Math.round(secsSince/60)}m ago`}
                                    </div>
                                  )}
                                  {la?.cnt != null && (
                                    <div style={{ color: C.muted, fontSize: 10, marginTop: 1 }}>
                                      Conditions: {la.cnt}/{s.reqCnt || "—"}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </Card>

                {/* Live Symbol WR */}
                {symbolWr && symbolWr.length > 0 && (
                  <Card title="Symbol Live Win Rates (trades.db)" icon="🎯" accent={C.amber}>
                    <div style={{ color: C.muted, fontSize: 12, marginBottom: 12 }}>
                      Actual Deriv contract outcomes — the only authoritative WR source.
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))", gap: 10 }}>
                      {symbolWr.map(s => {
                        const wr = s.win_rate_pct / 100;
                        const color = wr >= 0.8 ? C.green : wr >= 0.6 ? C.amber : C.red;
                        return (
                          <div key={s.symbol} style={{
                            background: C.panel, border: `2px solid ${color}33`,
                            borderRadius: 10, padding: "12px 14px", textAlign: "center",
                          }}>
                            <div style={{ color: C.cyan, fontFamily: "monospace", fontWeight: 800, fontSize: 14, marginBottom: 4 }}>
                              {s.symbol}
                            </div>
                            <div style={{ color, fontSize: 22, fontWeight: 800, fontFamily: "monospace" }}>
                              {s.win_rate_pct.toFixed(1)}%
                            </div>
                            <div style={{ color: C.dim, fontSize: 11, marginTop: 2 }}>{s.trades} trades</div>
                            <div style={{ color: s.pnl >= 0 ? C.green : C.red, fontSize: 12, fontFamily: "monospace", marginTop: 2 }}>
                              {s.pnl >= 0 ? "+" : ""}${s.pnl.toFixed(2)}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </Card>
                )}
              </>
            );
          })()}

          {/* Session performance */}
          {sessionStats && (() => {
            const sessArr = Array.isArray(sessionStats) ? sessionStats : Object.entries(sessionStats).map(([name, d]) => ({ session: name, ...d }));
            if (!sessArr.length) return null;
            return (
              <Card title="Session Performance" icon="🌍" accent={C.blue}>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 12 }}>
                  {sessArr.map(s => {
                    const wr = s.win_rate ?? (s.wins && s.total ? s.wins / s.total : null);
                    const color = wr != null ? (wr >= 0.8 ? C.green : wr >= 0.6 ? C.amber : C.red) : C.muted;
                    const name = s.session || s.name || "—";
                    return (
                      <div key={name} style={{
                        background: C.panel, border: `1px solid ${color}44`, borderRadius: 10, padding: "14px", textAlign: "center"
                      }}>
                        <div style={{ color: C.text, fontWeight: 700, fontSize: 13, marginBottom: 6 }}>{name}</div>
                        <div style={{ color, fontSize: 24, fontWeight: 800, fontFamily: "monospace" }}>
                          {wr != null ? `${(wr * 100).toFixed(1)}%` : "—"}
                        </div>
                        <div style={{ color: C.muted, fontSize: 11, marginTop: 2 }}>
                          {s.wins || 0}W / {s.losses || 0}L
                        </div>
                        {s.pnl != null && (
                          <div style={{ color: s.pnl >= 0 ? C.green : C.red, fontSize: 12, fontFamily: "monospace", marginTop: 2 }}>
                            {s.pnl >= 0 ? "+" : ""}${Number(s.pnl).toFixed(2)}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </Card>
            );
          })()}

          {/* 7-day WR trend from strategy_timeline */}
          {strategyTimeline && (() => {
            const rows = Array.isArray(strategyTimeline) ? strategyTimeline
              : Object.entries(strategyTimeline).flatMap(([strat, days]) =>
                  Array.isArray(days) ? days.map(d => ({ ...d, strategy: strat })) : []
                );
            const last7 = [...rows].sort((a, b) => (a.date || "").localeCompare(b.date || "")).slice(-14);
            if (!last7.length) return null;
            const maxTrades = Math.max(...last7.map(r => r.trades || 0), 1);
            return (
              <Card title="7-Day Trade Volume & WR Trend" icon="📈" accent={C.purple}>
                <div style={{ display: "flex", alignItems: "flex-end", gap: 4, height: 80, padding: "0 4px" }}>
                  {last7.map((r, i) => {
                    const wr = r.win_rate ?? (r.wins && r.trades ? r.wins / r.trades : null);
                    const h = Math.max(8, Math.round(((r.trades || 0) / maxTrades) * 70));
                    const color = wr != null ? (wr >= 0.8 ? C.green : wr >= 0.6 ? C.amber : C.red) : C.muted;
                    return (
                      <div key={i} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
                        <div style={{ color: color, fontSize: 9 }}>{wr != null ? `${(wr*100).toFixed(0)}%` : ""}</div>
                        <div style={{ width: "100%", height: h, background: color, borderRadius: "3px 3px 0 0", opacity: 0.7 }} />
                        <div style={{ color: C.dim, fontSize: 8, transform: "rotate(-45deg)", transformOrigin: "top center", marginTop: 2 }}>
                          {(r.date || "").slice(5)}
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div style={{ color: C.muted, fontSize: 11, marginTop: 20, textAlign: "center" }}>
                  Bar height = trade volume · Color = win rate (green ≥ 80%, amber ≥ 60%, red &lt; 60%)
                </div>
              </Card>
            );
          })()}
        </>
      )}

      {/* ════════════ ACCOUNT SETUP TAB ════════════ */}
      {section === "account" && (
        <>
          {/* Account Mode Switcher */}
          <Card title="Account Mode" icon="🔄">
            <div style={{ color: C.muted, fontSize: 13, marginBottom: 16 }}>
              Switch between paper trading (Demo) and real money (Live) mode.
              Live mode requires mission milestone (2+ certified strategies) + real account API token.
            </div>
            <div style={{ display: "flex", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
              <button
                onClick={() => handleSwitchMode("demo")}
                disabled={switchLoading || accountMode === "demo"}
                style={{
                  padding: "10px 28px", borderRadius: 8, border: `2px solid ${C.blue}`,
                  background: accountMode === "demo" ? C.blue : "transparent",
                  color: accountMode === "demo" ? "#fff" : C.blue,
                  fontWeight: 700, fontSize: 14, cursor: accountMode === "demo" ? "default" : "pointer",
                  opacity: switchLoading ? 0.6 : 1,
                }}
              >
                🔵 Demo Mode {accountMode === "demo" && "✓"}
              </button>
              <button
                onClick={() => handleSwitchMode("live")}
                disabled={switchLoading || accountMode === "live" || !liveUnlocked}
                style={{
                  padding: "10px 28px", borderRadius: 8,
                  border: `2px solid ${liveUnlocked ? C.red : C.muted}`,
                  background: accountMode === "live" ? C.red : "transparent",
                  color: accountMode === "live" ? "#fff" : liveUnlocked ? C.red : C.muted,
                  fontWeight: 700, fontSize: 14,
                  cursor: (accountMode === "live" || !liveUnlocked || switchLoading) ? "default" : "pointer",
                  opacity: (!liveUnlocked || switchLoading) ? 0.5 : 1,
                }}
              >
                🔴 Live Mode {accountMode === "live" && "✓"}
              </button>
            </div>
            {!liveUnlocked && (
              <div style={{
                background: C.amber + "15", border: `1px solid ${C.amber}33`,
                borderRadius: 8, padding: "10px 14px", color: C.amber, fontSize: 13,
              }}>
                {!missionComplete
                  ? `⚠️ Live mode unlocks when 2+ strategies are forge-certified (currently ${certifiedCount}/3).`
                  : "⚠️ Set your real account API token below to unlock live mode."}
              </div>
            )}
            {switchMsg && (
              <div style={{
                marginTop: 12, padding: "10px 14px", borderRadius: 8, fontSize: 13,
                background: switchMsg.type === "success" ? C.green + "15" : C.red + "15",
                color: switchMsg.type === "success" ? C.green : C.red,
                border: `1px solid ${switchMsg.type === "success" ? C.green : C.red}33`,
              }}>{switchMsg.text}</div>
            )}
          </Card>

          {/* Token Management */}
          <Card title="API Token Management" icon="🔑">
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 20 }}>
              {/* Demo token */}
              <div>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
                  <span style={{ color: C.blue, fontWeight: 700, fontSize: 13 }}>Demo Account Token</span>
                  <Badge color={hasDemoToken ? C.green : C.muted}>{hasDemoToken ? "SET ✓" : "NOT SET"}</Badge>
                </div>
                <div style={{ color: C.muted, fontSize: 12, marginBottom: 10 }}>
                  Your Deriv virtual/demo account API token for paper trading.
                </div>
                <input
                  type="password"
                  placeholder="Paste demo token here..."
                  value={demoToken}
                  onChange={e => setDemoToken(e.target.value)}
                  onKeyDown={e => e.key === "Enter" && handleSetDemoToken()}
                  style={{
                    width: "100%", padding: "8px 12px", borderRadius: 6,
                    border: `1px solid ${C.border}`, background: C.panel, color: C.text,
                    fontSize: 13, boxSizing: "border-box", marginBottom: 8,
                  }}
                />
                <button
                  onClick={handleSetDemoToken}
                  disabled={loading || !demoToken.trim()}
                  style={{
                    width: "100%", padding: "8px 0", borderRadius: 6, border: `1px solid ${C.blue}`,
                    background: demoToken.trim() ? C.blue : "transparent",
                    color: demoToken.trim() ? "#fff" : C.muted,
                    fontWeight: 700, fontSize: 13, cursor: demoToken.trim() ? "pointer" : "default",
                    opacity: loading ? 0.6 : 1,
                  }}
                >
                  {loading ? "Verifying..." : "Save Demo Token"}
                </button>
              </div>

              {/* Live token */}
              <div>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
                  <span style={{ color: C.red, fontWeight: 700, fontSize: 13 }}>Live Account Token</span>
                  <Badge color={hasLiveToken ? C.green : C.muted}>{hasLiveToken ? "SET ✓" : "NOT SET"}</Badge>
                </div>
                <div style={{ color: C.muted, fontSize: 12, marginBottom: 10 }}>
                  Your Deriv real money account API token. Must have <b>Read</b> + <b>Trade</b> permissions.
                  Kara verifies this is a real (non-virtual) account before saving.
                </div>
                <input
                  type="password"
                  placeholder="Paste live token here..."
                  value={liveToken}
                  onChange={e => setLiveToken(e.target.value)}
                  onKeyDown={e => e.key === "Enter" && handleSetLiveToken()}
                  style={{
                    width: "100%", padding: "8px 12px", borderRadius: 6,
                    border: `1px solid ${C.border}`, background: C.panel, color: C.text,
                    fontSize: 13, boxSizing: "border-box", marginBottom: 8,
                  }}
                />
                <button
                  onClick={handleSetLiveToken}
                  disabled={loading || !liveToken.trim()}
                  style={{
                    width: "100%", padding: "8px 0", borderRadius: 6, border: `1px solid ${C.red}`,
                    background: liveToken.trim() ? C.red : "transparent",
                    color: liveToken.trim() ? "#fff" : C.muted,
                    fontWeight: 700, fontSize: 13, cursor: liveToken.trim() ? "pointer" : "default",
                    opacity: loading ? 0.6 : 1,
                  }}
                >
                  {loading ? "Verifying with Deriv..." : "Save & Verify Live Token"}
                </button>
              </div>
            </div>

            {tokenMsg && (
              <div style={{
                marginTop: 14, padding: "10px 14px", borderRadius: 8, fontSize: 13,
                background: tokenMsg.type === "success" ? C.green + "15" : C.red + "15",
                color: tokenMsg.type === "success" ? C.green : C.red,
                border: `1px solid ${tokenMsg.type === "success" ? C.green : C.red}33`,
              }}>{tokenMsg.text}</div>
            )}
          </Card>

          {/* ── Auto-Optimize Button ─────────────────────────────────────── */}
          <Card title="Auto-Setup for Profitability" icon="🚀" accent={C.amber}>
            {/* Live Readiness Checklist */}
            {(() => {
              const activeStrat = Object.keys(strategyStatus || {}).find(k =>
                (strategyStatus[k]?.pnl > 0 || strategyStatus[k]?.win_rate >= 0.55)
              ) || "JD_NIGHT_OWL_URUS.md";
              const activeLabel = activeStrat ? activeStrat.replace(/\.md$/, "") : "None";
              const checks = [
                { label: `Strategy active (${activeLabel})`, done: !!activeStrat },
                { label: "API token set", done: hasDemoToken || hasLiveToken },
                { label: "Auto-optimize applied", done: !!autoOptResult },
                { label: "Auto-trade enabled", done: false, note: "Enable in Settings tab after setup" },
              ];
              return (
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 16 }}>
                  {checks.map(c => (
                    <div key={c.label} style={{
                      display: "flex", alignItems: "center", gap: 6,
                      background: c.done ? C.green + "15" : C.panel,
                      border: `1px solid ${c.done ? C.green + "44" : C.border}`,
                      borderRadius: 6, padding: "6px 10px", fontSize: 12,
                    }}>
                      <span style={{ color: c.done ? C.green : C.dim }}>{c.done ? "✓" : "○"}</span>
                      <span style={{ color: c.done ? C.text : C.muted }}>{c.label}</span>
                      {c.note && <span style={{ color: C.dim, fontSize: 11 }}>— {c.note}</span>}
                    </div>
                  ))}
                </div>
              );
            })()}

            <div style={{ color: C.muted, fontSize: 13, marginBottom: 16, lineHeight: 1.5 }}>
              Analyzes your real trade history, live win rates by hour, and account balance to automatically
              configure the optimal stake, trading hours, and risk limits. One click — no manual tuning needed.
            </div>

            <button
              onClick={async () => {
                setAutoOptLoading(true);
                setAutoOptResult(null);
                setAutoOptError(null);
                try {
                  const r = await fetch("/api/scanner/auto-optimize", {
                    method: "POST", headers: authHeaders,
                  });
                  const data = await r.json();
                  if (!r.ok) throw new Error(data.error || "Auto-optimize failed");
                  setAutoOptResult(data);
                } catch (e) {
                  setAutoOptError(e.message);
                } finally {
                  setAutoOptLoading(false);
                }
              }}
              disabled={autoOptLoading}
              style={{
                padding: "12px 32px", borderRadius: 8, border: "none",
                background: autoOptLoading ? C.dim : C.amber,
                color: "#000", fontWeight: 700, fontSize: 15,
                cursor: autoOptLoading ? "default" : "pointer",
                marginBottom: 16,
              }}
            >
              {autoOptLoading ? "⏳ Analyzing your data…" : "🚀 Auto-Setup for Profitability"}
            </button>

            {autoOptError && (
              <div style={{ color: C.red, fontSize: 13, marginBottom: 12 }}>❌ {autoOptError}</div>
            )}

            {autoOptResult && (
              <div style={{
                background: C.green + "10", border: `1px solid ${C.green}33`,
                borderRadius: 8, padding: "14px 16px",
              }}>
                <div style={{ color: C.green, fontWeight: 700, marginBottom: 10, fontSize: 14 }}>
                  ✅ Settings applied based on your live trade data
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 10, marginBottom: 12 }}>
                  {[
                    { label: "Strategy", value: autoOptResult.applied?.strategy?.replace(".md", "") },
                    { label: "Stake per trade", value: `$${autoOptResult.applied?.stake}` },
                    { label: "Max daily loss", value: `$${autoOptResult.applied?.riskConfig?.maxDailyLoss}` },
                    { label: "Max open trades", value: autoOptResult.applied?.riskConfig?.maxOpenTrades },
                    { label: "TP/SL monitor", value: "OFF — ride to expiry", note: "Full binary payout" },
                    {
                      label: "Trading hours (UTC)",
                      value: autoOptResult.applied?.tradingHours?.enabled
                        ? `${autoOptResult.applied.tradingHours.allowedHours?.length} hours active`
                        : "All hours (building data)",
                    },
                  ].map(({ label, value, note }) => (
                    <div key={label} style={{ background: C.panel, borderRadius: 6, padding: "10px 12px" }}>
                      <div style={{ color: C.muted, fontSize: 11, textTransform: "uppercase" }}>{label}</div>
                      <div style={{ color: C.green, fontWeight: 700, fontSize: 14, marginTop: 2 }}>{value}</div>
                      {note && <div style={{ color: C.dim, fontSize: 11 }}>{note}</div>}
                    </div>
                  ))}
                </div>
                {autoOptResult.applied?.tradingHours?.enabled && (
                  <div style={{ color: C.muted, fontSize: 12, marginBottom: 8 }}>
                    Active hours: {autoOptResult.applied.tradingHours.allowedHours?.map(h => `${String(h).padStart(2,"0")}:00`).join(", ")}
                  </div>
                )}
                <div style={{ color: C.dim, fontSize: 11, lineHeight: 1.5 }}>
                  <div>📊 {autoOptResult.reasoning?.stake}</div>
                  <div style={{ marginTop: 4 }}>⏰ {autoOptResult.reasoning?.hours}</div>
                  <div style={{ marginTop: 4 }}>🛡️ {autoOptResult.reasoning?.risk}</div>
                  <div style={{ marginTop: 4 }}>💡 {autoOptResult.reasoning?.tp_sl}</div>
                </div>
                <div style={{ color: C.muted, fontSize: 11, marginTop: 10 }}>
                  Based on {autoOptResult.reasoning?.data?.total_trades || 0} live trades
                  {autoOptResult.reasoning?.data?.live_wr != null &&
                    ` · ${(autoOptResult.reasoning.data.live_wr * 100).toFixed(1)}% live WR`}
                  {autoOptResult.reasoning?.data?.balance > 0 &&
                    ` · $${autoOptResult.reasoning.data.balance.toFixed(0)} balance`}
                </div>
              </div>
            )}
          </Card>
        </>
      )}

      {/* ════════════ DERIV API GUIDE TAB ════════════ */}
      {section === "deriv" && (
        <>
          <Card title="Getting Your Deriv API Tokens" icon="🔑">
            <div style={{
              background: C.amber + "15", border: `1px solid ${C.amber}33`,
              borderRadius: 8, padding: "12px 16px", marginBottom: 20, color: C.amber, fontSize: 13,
            }}>
              ⚠️ <b>Important:</b> Deriv has updated their API platform to OAuth 2.0 + Personal Access Tokens (PAT).
              New token format starts with <code style={{ background: C.panel, padding: "1px 6px", borderRadius: 4 }}>pat_</code>.
              Both PAT and legacy tokens work with Kara Scanner — the system auto-detects the type.
            </div>

            <h4 style={{ color: C.blue, marginBottom: 12, fontSize: 14 }}>Demo Account Token</h4>
            <Step n={1} title="Log in to Deriv">
              Visit <b style={{ color: C.amber }}>app.deriv.com</b> and sign in to your account.
            </Step>
            <Step n={2} title="Switch to your Virtual account">
              Click the account switcher in the top-right corner and select your <b style={{ color: C.blue }}>Virtual (Demo)</b> account.
              The balance will show "Demo" in the header.
            </Step>
            <Step n={3} title="Open API Token settings">
              Click your profile name / avatar → <b style={{ color: C.amber }}>Account Settings</b> →
              in the left sidebar, scroll to <b style={{ color: C.amber }}>API Token</b>.
            </Step>
            <Step n={4} title="Create a new token">
              Click <b style={{ color: C.amber }}>+ Create new token</b>. Name it <code style={{ background: C.panel, padding: "1px 5px", borderRadius: 3 }}>Kara Scanner Demo</code>.
              Enable these scopes: <b style={{ color: C.amber }}>Read</b>, <b style={{ color: C.amber }}>Trade</b>, <b style={{ color: C.amber }}>Payments</b>.
              Click <b>Create</b>.
            </Step>
            <Step n={5} title="Copy the token">
              Click the copy icon next to your new token. Paste it in the <b style={{ color: C.blue }}>Demo Account Token</b> field in the Account Setup section above.
            </Step>

            <div style={{ borderTop: `1px solid ${C.border}`, margin: "20px 0" }} />

            <h4 style={{ color: C.red, marginBottom: 12, fontSize: 14 }}>Real (Live) Account Token</h4>
            <div style={{
              background: C.red + "10", border: `1px solid ${C.red}33`, borderRadius: 8,
              padding: "10px 14px", marginBottom: 16, color: C.red, fontSize: 13,
            }}>
              🔴 <b>Real money at risk.</b> Only set this after mission milestone is achieved and you have tested the strategy in demo for at least 2 weeks.
            </div>
            <Step n={1} title="Switch to your Real account">
              In the Deriv app, click the account switcher and select your <b style={{ color: C.red }}>Real</b> account
              (NOT Virtual). The header will show your real account ID (e.g. CR123456).
            </Step>
            <Step n={2} title="Create a Real account API token">
              Go to Account Settings → API Token → Create new token.
              Name: <code style={{ background: C.panel, padding: "1px 5px", borderRadius: 3 }}>Kara Live</code>.
              Enable: <b style={{ color: C.amber }}>Read</b>, <b style={{ color: C.amber }}>Trade</b>, <b style={{ color: C.amber }}>Payments</b>.
            </Step>
            <Step n={3} title="Set and verify in Kara">
              Paste the real account token in the <b style={{ color: C.red }}>Live Account Token</b> field (Account Setup section).
              Click <b>Save &amp; Verify</b>. Kara connects to Deriv and confirms it is a real (non-virtual) account before saving.
              It will reject virtual account tokens automatically.
            </Step>
            <Step n={4} title="Switch to Live Mode">
              Once the token is verified and the mission milestone is reached, the Live Mode button will unlock.
              You will see a confirmation prompt with a risk disclaimer before switching.
            </Step>

            <div style={{ borderTop: `1px solid ${C.border}`, margin: "20px 0" }} />

            <h4 style={{ color: C.purple, marginBottom: 12, fontSize: 14 }}>New Deriv Platform (PAT Tokens)</h4>
            <div style={{ color: C.muted, fontSize: 13, lineHeight: 1.7, marginBottom: 12 }}>
              Deriv's new platform uses <b style={{ color: C.amber }}>Personal Access Tokens (PAT)</b> starting with <code style={{ background: C.panel, padding: "1px 6px", borderRadius: 4 }}>pat_</code>.
              These use a different authentication flow (OAuth 2.0 + REST + WebSocket hybrid).
            </div>
            <Step n={1} title="Visit developers.deriv.com">
              Log in with your Deriv credentials at the developer portal.
            </Step>
            <Step n={2} title="Generate a PAT">
              Under <b>My Tokens</b> or <b>Personal Access Tokens</b>, create a new token.
              Select scopes: <b style={{ color: C.amber }}>trading:read</b>, <b style={{ color: C.amber }}>trading:write</b>, <b style={{ color: C.amber }}>account:read</b>.
            </Step>
            <Step n={3} title="Use in Kara">
              Paste the <code style={{ background: C.panel, padding: "1px 5px", borderRadius: 3 }}>pat_...</code> token into either the Demo or Live token field.
              Kara detects PAT tokens automatically and uses the new Deriv REST API endpoint for authorization.
            </Step>
          </Card>

          <Card title="App Registration (Optional — Advanced)" icon="🏗">
            <div style={{ color: C.muted, fontSize: 13, lineHeight: 1.7 }}>
              <p>
                Kara uses <b style={{ color: C.amber }}>App ID 1089</b> (Deriv's public test app) by default.
                For production, register your own app to get higher rate limits and your own OAuth flow:
              </p>
              <ol style={{ paddingLeft: 20, margin: "12px 0", lineHeight: 2 }}>
                <li>Go to <b>developers.deriv.com</b> → <b>Register App</b></li>
                <li>Set scopes: read, trade, payments, admin</li>
                <li>Set redirect URI: <code style={{ background: C.panel, padding: "1px 6px", borderRadius: 4 }}>http://your-server-ip:4000</code></li>
                <li>Copy your App ID and set <b style={{ color: C.amber }}>DERIV_APP_ID=your_id</b> in <code>/opt/ai-stack/lalascanner/lala-scanner/.env</code></li>
                <li>Restart: <code style={{ background: C.panel, padding: "1px 6px", borderRadius: 4 }}>pm2 restart kara-scanner</code></li>
              </ol>
            </div>
          </Card>

          <Card title="Security Best Practices" icon="🔒">
            <div style={{ color: C.muted, fontSize: 13, lineHeight: 1.9 }}>
              <ul style={{ paddingLeft: 20, margin: 0 }}>
                <li><b style={{ color: C.text }}>Tokens are server-side only</b> — stored in the server's users.json, never sent to the browser or logged.</li>
                <li><b style={{ color: C.text }}>Never share tokens</b> — they provide full account access. Treat them like passwords.</li>
                <li><b style={{ color: C.text }}>Demo ≠ Live tokens</b> — they connect to separate account types. Kara validates before saving.</li>
                <li><b style={{ color: C.text }}>Token expiry</b> — if a trade returns 401 Unauthorized, Kara pauses auto-trade and alerts you via Telegram to refresh the token.</li>
                <li><b style={{ color: C.text }}>Revoke anytime</b> — delete tokens in Deriv's API Token settings if you suspect unauthorized use.</li>
                <li><b style={{ color: C.text }}>Max daily loss guard</b> — even in live mode, the scanner's daily loss limit stops all trading if hit.</li>
              </ul>
            </div>
          </Card>
        </>
      )}

      {/* ════════════ HOW TO USE TAB ════════════ */}
      {section === "guide" && (
        <>
          <Card title="Kara AI Scanner — Complete Feature Guide" icon="📚">
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 16 }}>
              {[
                {
                  icon: "📊", title: "Scanner Tab", color: C.green,
                  desc: "Main trading dashboard. Live signals for your active symbols. Each card shows signal count, direction, and indicator values. Signals fire when all required conditions align simultaneously.",
                },
                {
                  icon: "🗂", title: "Strategy Tab", color: C.amber,
                  desc: "Manage trading strategies. Activate forge-certified strategies, view their allowed symbols, best hours, and forge gates. Approved: 90_STRATEGY, KARA_APEX_ASYMMETRIC_350, JD_NIGHT_OWL_URUS, LIVE_EDGE_BASKET (Rule #9 active set, revised 2026-08-31).",
                },
                {
                  icon: "🧪", title: "Backtests Tab", color: C.purple,
                  desc: "Run Optuna optimization (100–1000 trials), walk-forward analysis (5 splits), and Monte Carlo simulations (1000 runs). Always set min_win_rate=0.80 and min_trades=10.",
                },
                {
                  icon: "📋", title: "History Tab", color: C.blue,
                  desc: "Full trade history with P&L. Sync button resolves pending outcomes. All trades are stored in SQLite permanently — nothing is deleted. Filter by symbol, strategy, outcome.",
                },
                {
                  icon: "⚙️", title: "Settings Tab", color: C.cyan,
                  desc: "Configure stake, max daily loss, ML confidence, auto-trade, trading hours, and per-symbol selections. Risk config changes are immediate — no restart needed.",
                },
                {
                  icon: "📈", title: "Charts Tab", color: C.amber,
                  desc: "Live multi-symbol charts. Up to 4 simultaneous chart windows in single/dual/quad layouts. Click 'Ask Kara' to analyze price action with AI.",
                },
                {
                  icon: "📖", title: "Info Tab (this page)", color: C.muted,
                  desc: "Personalized dashboard: mission status, P&L, strategy performance, API token setup, live account management, and platform guide. Unique per user.",
                },
                {
                  icon: "🤖", title: "ML Signals", color: C.green,
                  desc: "Machine learning gate on top of technical signals. Trained on your trade history. Enable ML Auto-Trade + set confidence ≥ 65% for best results.",
                },
              ].map(({ icon, title, color, desc }) => (
                <div key={title} style={{
                  background: C.panel, border: `1px solid ${color}22`, borderRadius: 8, padding: "14px 16px",
                }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                    <span style={{ fontSize: 18 }}>{icon}</span>
                    <span style={{ color, fontWeight: 700, fontSize: 14 }}>{title}</span>
                  </div>
                  <p style={{ margin: 0, color: C.muted, fontSize: 12, lineHeight: 1.6 }}>{desc}</p>
                </div>
              ))}
            </div>
          </Card>

          <Card title="Auto-Trade Quickstart" icon="🤖">
            <Step n={1} title="Connect your Deriv account">
              Get an API token from app.deriv.com (Account Settings → API Token) and paste it in the
              <b style={{ color: C.amber }}> Account Setup</b> section of this page.
            </Step>
            <Step n={2} title="Enable Auto-Trade in Settings">
              Settings tab → toggle <b style={{ color: C.amber }}>Auto-Trade ON</b>.
              Optionally enable <b style={{ color: C.amber }}>ML Auto-Trade</b> for a second AI filter.
            </Step>
            <Step n={3} title="Set your stake and risk limits">
              Settings tab: set <b style={{ color: C.amber }}>Stake</b> (e.g. $5), <b style={{ color: C.amber }}>Max Daily Loss</b> (e.g. $30),
              and <b style={{ color: C.amber }}>Max Open Trades</b> (e.g. 3).
            </Step>
            <Step n={4} title="Activate a certified strategy">
              Strategy tab → select your active strategy (Kenyon: JD_NIGHT_OWL_URUS; Lando: LIVE_EDGE_BASKET) → click Activate.
              The scanner auto-applies the right symbols and trading hours.
            </Step>
            <Step n={5} title="Monitor and review daily">
              Check the History tab each day. Target ≥ 80% win rate. If WR drops below 60% for 20+ trades,
              the system will flag the strategy and you should pause auto-trade and review.
            </Step>
          </Card>

          <Card title="Risk Management Rules" icon="🛡">
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 14 }}>
              {[
                { label: "Stake per Trade", tip: "2–5% of account max. Never exceed 10% on a single trade." },
                { label: "Max Daily Loss", tip: "Set to 5–10% of balance. The scanner stops automatically when hit." },
                { label: "Max Open Trades", tip: "3 or fewer. More = higher correlation risk during volatile sessions." },
                { label: "Win Rate Alert", tip: "If 7-day WR drops below 60%, review live vs backtest gap and pause." },
                { label: "Dead Zone Hours", tip: "Per-active-strategy hour whitelist (system reads from params.json). System auto-skips non-whitelisted hours and surfaces them in this tab. Example: JD_NIGHT_OWL_URUS best_hours = [2, 7, 11, 13, 14] UTC." },
                { label: "Symbol Selection", tip: "Stick to strategy's allowed_symbols. Don't trade unsupported pairs." },
              ].map(({ label, tip }) => (
                <div key={label} style={{
                  background: C.panel, border: `1px solid ${C.border}`, borderRadius: 8, padding: "12px 14px",
                }}>
                  <div style={{ color: C.amber, fontWeight: 700, fontSize: 13, marginBottom: 6 }}>{label}</div>
                  <div style={{ color: C.muted, fontSize: 12, lineHeight: 1.5 }}>{tip}</div>
                </div>
              ))}
            </div>
          </Card>

          <Card title="Kara AI Voice Commands" icon="🎙">
            <div style={{ color: C.muted, fontSize: 13, lineHeight: 1.6, marginBottom: 12 }}>
              Say <b style={{ color: C.amber }}>"Kara"</b> (or <b style={{ color: C.blue }}>"Lala"</b> for Lando) to activate voice mode.
              Kara can execute trades, check balance, switch strategies, and more via voice.
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
              {[
                ["\"Kara, what's my balance?\"", "Fetches live Deriv balance"],
                ["\"Kara, show me the charts\"", "Switches to Charts tab"],
                ["\"Kara, pause auto-trade\"", "Disables automatic trading"],
                ["\"Kara, what's my win rate?\"", "Reports today's performance"],
                ["\"Kara, check system health\"", "Runs health check on all services"],
                ["\"Kara, shut up\"", "Stops voice mode and returns to wake-word mode"],
              ].map(([cmd, desc]) => (
                <div key={cmd} style={{ background: C.panel, borderRadius: 6, padding: "8px 12px" }}>
                  <div style={{ color: C.green, fontFamily: "monospace", fontSize: 12, marginBottom: 2 }}>{cmd}</div>
                  <div style={{ color: C.muted, fontSize: 11 }}>{desc}</div>
                </div>
              ))}
            </div>
          </Card>
        </>
      )}

      {/* Independent research monitor; the scanner trading pipeline is unchanged. */}

      {/* ════════════ MT5 & RDP SETUP TAB ════════════ */}
      {section === "mt5" && (
        <>
          <Card title="MetaTrader 5 & Remote Desktop (RDP) Integration Guide" icon="🖥️" accent={C.blue}>
            <p style={{ color: C.muted, fontSize: 13, lineHeight: 1.6, margin: "0 0 16px 0" }}>
              Kara AI connects directly with MetaTrader 5 (MT5) running on the server under Wine via the <b style={{ color: C.blue }}>KaraBridge EA</b>.
              Follow this step-by-step guide to connect via RDP, complete the MT5 graphical installation, attach the EA, and start automated trading for Deriv MT5 or prop firm challenges (e.g. FundedNext).
            </p>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 12 }}>
              <div style={{ background: C.panel, border: `1px solid ${C.border}`, borderRadius: 8, padding: "12px 14px" }}>
                <div style={{ color: C.muted, fontSize: 11, textTransform: "uppercase", letterSpacing: 1 }}>Server Host</div>
                <div style={{ color: C.amber, fontSize: 16, fontWeight: 800, fontFamily: "monospace", marginTop: 4 }}>lala.kara-ai.cloud</div>
                <div style={{ color: C.dim, fontSize: 11, marginTop: 2 }}>IP: 187.124.159.32</div>
              </div>
              <div style={{ background: C.panel, border: `1px solid ${C.border}`, borderRadius: 8, padding: "12px 14px" }}>
                <div style={{ color: C.muted, fontSize: 11, textTransform: "uppercase", letterSpacing: 1 }}>RDP Port</div>
                <div style={{ color: C.cyan, fontSize: 16, fontWeight: 800, fontFamily: "monospace", marginTop: 4 }}>3389</div>
                <div style={{ color: C.dim, fontSize: 11, marginTop: 2 }}>Protocol: XRDP (LXDE Desktop)</div>
              </div>
              <div style={{ background: C.panel, border: `1px solid ${C.border}`, borderRadius: 8, padding: "12px 14px" }}>
                <div style={{ color: C.muted, fontSize: 11, textTransform: "uppercase", letterSpacing: 1 }}>RDP Username</div>
                <div style={{ color: C.green, fontSize: 16, fontWeight: 800, fontFamily: "monospace", marginTop: 4 }}>root</div>
                <div style={{ color: C.dim, fontSize: 11, marginTop: 2 }}>Password: [Server root pass]</div>
              </div>
              <div style={{ background: C.panel, border: `1px solid ${C.border}`, borderRadius: 8, padding: "12px 14px" }}>
                <div style={{ color: C.muted, fontSize: 11, textTransform: "uppercase", letterSpacing: 1 }}>Bridge Architecture</div>
                <div style={{ color: C.purple, fontSize: 16, fontWeight: 800, fontFamily: "monospace", marginTop: 4 }}>Port 5001 ⇆ 8000</div>
                <div style={{ color: C.dim, fontSize: 11, marginTop: 2 }}>KaraBridge.mq5 Socket Listener</div>
              </div>
            </div>
          </Card>

          <Card title="Step-by-Step MT5 Installation & Setup" icon="🛠️">
            <Step n={1} title="Connect to the Linux Server via Remote Desktop (RDP)">
              <div style={{ marginBottom: 6 }}>Open your Remote Desktop application (Windows Remote Desktop Connection, Microsoft Remote Desktop on Mac, or Remmina on Linux):</div>
              <div style={{ background: C.panel, border: `1px solid ${C.border}`, borderRadius: 6, padding: "8px 12px", fontFamily: "monospace", fontSize: 12, color: C.text, marginBottom: 8 }}>
                Computer: <span style={{ color: C.amber }}>lala.kara-ai.cloud:3389</span> (or <span style={{ color: C.amber }}>187.124.159.32:3389</span>)<br />
                Username: <span style={{ color: C.green }}>root</span><br />
                Password: <span style={{ color: C.muted }}>[Your server root password]</span>
              </div>
              <div>When connected, you will see the LXDE desktop environment.</div>
            </Step>

            <Step n={2} title="Run the MT5 Installer">
              <div style={{ marginBottom: 6 }}>On the LXDE desktop screen, double-click the <b style={{ color: C.amber }}>Install_MT5.sh</b> icon, or open the desktop terminal and run:</div>
              <div style={{ background: C.panel, border: `1px solid ${C.border}`, borderRadius: 6, padding: "8px 12px", fontFamily: "monospace", fontSize: 12, color: C.text, marginBottom: 8 }}>
                bash /root/Desktop/Install_MT5.sh
              </div>
              <div style={{ color: C.muted }}>
                • Follow the standard MetaTrader 5 setup wizard on screen.<br />
                • Accept the default install path: <code style={{ color: C.cyan }}>C:\Program Files\MetaTrader 5\</code>.<br />
                • Once the installation finishes, the MT5 terminal will open automatically.
              </div>
            </Step>

            <Step n={3} title="Log in to your Broker or Funded Account in MT5">
              <div style={{ marginBottom: 6 }}>
                In the MT5 terminal, select <b>File → Login to Trade Account</b>:
              </div>
              <ul style={{ margin: "0 0 8px 18px", padding: 0 }}>
                <li><b>Login:</b> Your Deriv MT5 login or Prop firm account number.</li>
                <li><b>Password:</b> Your trading account password.</li>
                <li><b>Server:</b> Select your broker's server (e.g. <code style={{ color: C.amber }}>Deriv-Server</code> or <code style={{ color: C.blue }}>FundedNext-Server</code>).</li>
              </ul>
              <div>Ensure tick data is streaming (the connection bars at bottom right should show green/blue).</div>
            </Step>

            <Step n={4} title="Install and Compile KaraBridge EA">
              <div style={{ marginBottom: 6 }}>The KaraBridge EA file is pre-built on the server. Copy it into MT5's Experts directory:</div>
              <div style={{ background: C.panel, border: `1px solid ${C.border}`, borderRadius: 6, padding: "8px 12px", fontFamily: "monospace", fontSize: 12, color: C.text, marginBottom: 8 }}>
                MT5_EXPERTS=$(find /root/.mt5 -path "*/MQL5/Experts" -type d 2&gt;/dev/null | head -1)<br />
                cp /opt/ai-stack/mt5_ea/KaraBridge.mq5 "$MT5_EXPERTS/"<br />
                echo "Copied to: $MT5_EXPERTS"
              </div>
              <div style={{ marginBottom: 6 }}>Now in MT5:</div>
              <ol style={{ margin: "0 0 8px 18px", padding: 0 }}>
                <li>Open <b>MetaEditor</b> by pressing <kbd style={{ background: C.panel, border: `1px solid ${C.border}`, padding: "1px 6px", borderRadius: 4, color: C.amber }}>F4</kbd> (or menu <b>Tools → MetaQuotes Language Editor</b>).</li>
                <li>In Navigator on left, expand <b>Experts</b> and double-click <b>KaraBridge.mq5</b>.</li>
                <li>Press <kbd style={{ background: C.panel, border: `1px solid ${C.border}`, padding: "1px 6px", borderRadius: 4, color: C.amber }}>F7</kbd> to compile. It should output <b>0 errors, 0 warnings</b>.</li>
                <li>Close MetaEditor and return to the main MT5 terminal.</li>
              </ol>
            </Step>

            <Step n={5} title="Attach KaraBridge EA to an Active Chart">
              <ol style={{ margin: "0 0 8px 18px", padding: 0 }}>
                <li>Open any live chart (e.g. <b>EURUSD M1</b> or <b>Volatility 75 Index M1</b>).</li>
                <li>From the <b>Navigator</b> panel (left), drag <b>KaraBridge</b> onto the chart.</li>
                <li>In the EA settings popup:
                  <ul style={{ marginTop: 4 }}>
                    <li><b>Port:</b> <code style={{ color: C.cyan }}>8000</code></li>
                    <li><b>Token:</b> <code style={{ color: C.green }}>kara_mt5_local</code></li>
                    <li><b>MaxLot:</b> <code style={{ color: C.amber }}>1.0</code> (or your custom safety cap)</li>
                  </ul>
                </li>
                <li>In the <b>Common</b> tab, check <b style={{ color: C.green }}>☑ Allow Algo Trading</b>.</li>
                <li>Click the top toolbar button <b style={{ color: C.green }}>Algo Trading</b> so it displays a green play icon.</li>
                <li>Check the <b>Experts</b> tab at the bottom — it must display: <span style={{ color: C.green, fontFamily: "monospace" }}>"KaraBridge: Listening on port 8000 — Kara AI ready."</span></li>
              </ol>
            </Step>

            <Step n={6} title="Verify Bridge Status and Automation">
              <div style={{ marginBottom: 6 }}>Check the bridge connectivity from terminal or the <b style={{ color: C.blue }}>MT5 Funded Tab</b> in this dashboard:</div>
              <div style={{ background: C.panel, border: `1px solid ${C.border}`, borderRadius: 6, padding: "8px 12px", fontFamily: "monospace", fontSize: 12, color: C.text, marginBottom: 8 }}>
                curl http://localhost:8000/health<br />
                # Output: {'{"status":"ok","account":"12345678"}'}<br /><br />
                curl http://localhost:5001/health<br />
                # Output: {'{"status":"ok","mt5_rest_url":"http://localhost:8000","mt5_ok":true}'}
              </div>
              <div style={{ color: C.muted }}>Once verified, the scanner, Telegram Bot, and MT5 Funded Tab can route orders directly to your MT5 terminal in real time!</div>
            </Step>
          </Card>

          <Card title="Troubleshooting & Useful Commands" icon="🔧">
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 14 }}>
              <div style={{ background: C.panel, border: `1px solid ${C.border}`, borderRadius: 8, padding: "14px 16px" }}>
                <div style={{ color: C.amber, fontWeight: 700, fontSize: 13, marginBottom: 6 }}>Restart / Launch MT5 Manually</div>
                <div style={{ color: C.muted, fontSize: 12, lineHeight: 1.5, marginBottom: 8 }}>
                  Double click <b>Launch_MT5.sh</b> on Desktop, or execute:
                </div>
                <code style={{ background: C.bg, display: "block", padding: "6px 8px", borderRadius: 4, fontSize: 11, color: C.cyan }}>
                  bash /root/Desktop/Launch_MT5.sh
                </code>
              </div>

              <div style={{ background: C.panel, border: `1px solid ${C.border}`, borderRadius: 8, padding: "14px 16px" }}>
                <div style={{ color: C.amber, fontWeight: 700, fontSize: 13, marginBottom: 6 }}>Clear Hung Wine Subprocess</div>
                <div style={{ color: C.muted, fontSize: 12, lineHeight: 1.5, marginBottom: 8 }}>
                  If the installer warns of a subprocess or lock:
                </div>
                <code style={{ background: C.bg, display: "block", padding: "6px 8px", borderRadius: 4, fontSize: 11, color: C.cyan }}>
                  wineserver -k
                </code>
              </div>

              <div style={{ background: C.panel, border: `1px solid ${C.border}`, borderRadius: 8, padding: "14px 16px" }}>
                <div style={{ color: C.amber, fontWeight: 700, fontSize: 13, marginBottom: 6 }}>Restart Kara MT5 Bridge Service</div>
                <div style={{ color: C.muted, fontSize: 12, lineHeight: 1.5, marginBottom: 8 }}>
                  Restart the Python REST bridge via PM2:
                </div>
                <code style={{ background: C.bg, display: "block", padding: "6px 8px", borderRadius: 4, fontSize: 11, color: C.cyan }}>
                  pm2 restart kara-mt5-bridge
                </code>
              </div>

              <div style={{ background: C.panel, border: `1px solid ${C.border}`, borderRadius: 8, padding: "14px 16px" }}>
                <div style={{ color: C.amber, fontWeight: 700, fontSize: 13, marginBottom: 6 }}>Enable WebRequest in MT5</div>
                <div style={{ color: C.muted, fontSize: 12, lineHeight: 1.5 }}>
                  In MT5: <b>Tools → Options → Expert Advisors</b><br />
                  Enable <b>Allow WebRequest</b> and add <code style={{ color: C.amber }}>http://localhost:8000</code> and <code style={{ color: C.amber }}>http://localhost:5001</code>.
                </div>
              </div>
            </div>
          </Card>

          <Card title="KaraBridge.mq5 — Complete Expert Advisor Source Code" icon="📜" accent={C.green}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12, flexWrap: "wrap", gap: 8 }}>
              <div style={{ color: C.muted, fontSize: 13 }}>
                If you prefer to copy and paste the EA code directly into MetaEditor instead of using the terminal command:
              </div>
              <button
                onClick={() => {
                  navigator.clipboard.writeText(`//+------------------------------------------------------------------+
//| KaraBridge.mq5                                                   |
//| Kara AI — MT5 REST Bridge EA                                     |
//| Creates a local HTTP server on port 8000 that accepts trade      |
//| commands from the kara-mt5-bridge Python service.                |
//+------------------------------------------------------------------+
#property copyright "Kara AI"
#property version   "2.00"
#property strict

#include <Trade\\Trade.mqh>

// ── Config ────────────────────────────────────────────────────────
input int    InpPort       = 8000;           // Local HTTP listen port
input string InpToken      = "kara_mt5_local"; // Auth token
input bool   InpLogTrades  = true;           // Log every trade to Experts tab
input double InpMaxLot     = 1.0;            // Maximum lot size safety cap

// ── Globals ───────────────────────────────────────────────────────
CTrade  trade;
int     serverSocket = INVALID_HANDLE;
int     clientSocket = INVALID_HANDLE;

//+------------------------------------------------------------------+
//| Expert initialization                                            |
//+------------------------------------------------------------------+
int OnInit()
{
   serverSocket = SocketCreate();
   if(serverSocket == INVALID_HANDLE) {
      Print("KaraBridge: Failed to create socket. Error: ", GetLastError());
      return INIT_FAILED;
   }

   if(!SocketListen(serverSocket, "0.0.0.0", InpPort, 10)) {
      Print("KaraBridge: Failed to bind port ", InpPort, ". Error: ", GetLastError());
      SocketClose(serverSocket);
      return INIT_FAILED;
   }

   Print("KaraBridge: Listening on port ", InpPort, " — Kara AI ready.");
   trade.SetExpertMagicNumber(20260814);
   trade.SetDeviationInPoints(30);
   return INIT_SUCCEEDED;
}

//+------------------------------------------------------------------+
//| Expert deinitialization                                          |
//+------------------------------------------------------------------+
void OnDeinit(const int reason)
{
   if(serverSocket != INVALID_HANDLE) SocketClose(serverSocket);
   if(clientSocket != INVALID_HANDLE) SocketClose(clientSocket);
   Print("KaraBridge: Stopped.");
}

//+------------------------------------------------------------------+
//| Expert tick — poll for incoming connections                      |
//+------------------------------------------------------------------+
void OnTick()
{
   // Accept a pending connection (non-blocking)
   int conn = SocketAccept(serverSocket, 0);
   if(conn == INVALID_HANDLE) return;

   // Read HTTP request
   string req = "";
   uchar  buf[];
   ArrayResize(buf, 4096);
   int bytes = SocketRead(conn, buf, 4096, 200);
   if(bytes > 0) {
      ArrayResize(buf, bytes);
      req = CharArrayToString(buf);
   }

   // Route request
   string response = "";
   if(StringFind(req, "GET /health") >= 0) {
      response = BuildResponse(200, "{\\"status\\":\\"ok\\",\\"account\\":\\"" + IntegerToString(AccountInfoInteger(ACCOUNT_LOGIN)) + "\\"}");
   }
   else if(StringFind(req, "GET /account") >= 0) {
      response = HandleAccount();
   }
   else if(StringFind(req, "GET /positions") >= 0) {
      response = HandlePositions();
   }
   else if(StringFind(req, "POST /trade") >= 0) {
      // Check auth token
      if(StringFind(req, InpToken) < 0 && InpToken != "") {
         response = BuildResponse(401, "{\\"error\\":\\"Unauthorized\\"}");
      } else {
         response = HandleTrade(req);
      }
   }
   else {
      response = BuildResponse(404, "{\\"error\\":\\"Not found\\"}");
   }

   // Send response
   uchar out[];
   StringToCharArray(response, out, 0, StringLen(response));
   SocketSend(conn, out, ArraySize(out) - 1);
   SocketClose(conn);
}

//+------------------------------------------------------------------+
//| Build HTTP response                                              |
//+------------------------------------------------------------------+
string BuildResponse(int code, string body)
{
   string status = (code == 200) ? "200 OK" : (code == 401) ? "401 Unauthorized" : "404 Not Found";
   return "HTTP/1.1 " + status + "\\r\\n"
        + "Content-Type: application/json\\r\\n"
        + "Access-Control-Allow-Origin: *\\r\\n"
        + "Content-Length: " + IntegerToString(StringLen(body)) + "\\r\\n"
        + "Connection: close\\r\\n\\r\\n"
        + body;
}

//+------------------------------------------------------------------+
//| Account info handler                                             |
//+------------------------------------------------------------------+
string HandleAccount()
{
   string json = "{";
   json += "\\"login\\":" + IntegerToString(AccountInfoInteger(ACCOUNT_LOGIN)) + ",";
   json += "\\"balance\\":" + DoubleToString(AccountInfoDouble(ACCOUNT_BALANCE), 2) + ",";
   json += "\\"equity\\":" + DoubleToString(AccountInfoDouble(ACCOUNT_EQUITY), 2) + ",";
   json += "\\"margin\\":" + DoubleToString(AccountInfoDouble(ACCOUNT_MARGIN), 2) + ",";
   json += "\\"free_margin\\":" + DoubleToString(AccountInfoDouble(ACCOUNT_FREEMARGIN), 2) + ",";
   json += "\\"currency\\":\\"" + AccountInfoString(ACCOUNT_CURRENCY) + "\\",";
   json += "\\"server\\":\\"" + AccountInfoString(ACCOUNT_SERVER) + "\\"";
   json += "}";
   return BuildResponse(200, json);
}

//+------------------------------------------------------------------+
//| Open positions handler                                           |
//+------------------------------------------------------------------+
string HandlePositions()
{
   string json = "[";
   int total = PositionsTotal();
   for(int i = 0; i < total; i++) {
      ulong ticket = PositionGetTicket(i);
      if(i > 0) json += ",";
      json += "{";
      json += "\\"ticket\\":" + IntegerToString(ticket) + ",";
      json += "\\"symbol\\":\\"" + PositionGetString(POSITION_SYMBOL) + "\\",";
      json += "\\"type\\":" + IntegerToString(PositionGetInteger(POSITION_TYPE)) + ",";
      json += "\\"volume\\":" + DoubleToString(PositionGetDouble(POSITION_VOLUME), 2) + ",";
      json += "\\"open_price\\":" + DoubleToString(PositionGetDouble(POSITION_PRICE_OPEN), 5) + ",";
      json += "\\"sl\\":" + DoubleToString(PositionGetDouble(POSITION_SL), 5) + ",";
      json += "\\"tp\\":" + DoubleToString(PositionGetDouble(POSITION_TP), 5) + ",";
      json += "\\"profit\\":" + DoubleToString(PositionGetDouble(POSITION_PROFIT), 2);
      json += "}";
   }
   json += "]";
   return BuildResponse(200, json);
}

//+------------------------------------------------------------------+
//| Trade execution handler                                          |
//+------------------------------------------------------------------+
string HandleTrade(string req)
{
   // Extract JSON body (after \\r\\n\\r\\n)
   int bodyStart = StringFind(req, "\\r\\n\\r\\n");
   if(bodyStart < 0) return BuildResponse(400, "{\\"error\\":\\"No body\\"}");
   string body = StringSubstr(req, bodyStart + 4);

   // Parse fields from JSON body
   string symbol = ParseJsonStr(body, "symbol");
   string action = ParseJsonStr(body, "action");
   double volume = ParseJsonDbl(body, "volume", 0.01);
   double sl     = ParseJsonDbl(body, "sl", 0.0);
   double tp     = ParseJsonDbl(body, "tp", 0.0);

   if(symbol == "" || action == "") return BuildResponse(400, "{\\"error\\":\\"symbol and action required\\"}");
   if(volume > InpMaxLot) volume = InpMaxLot;

   // Normalise symbol (strip prefix if needed)
   if(!SymbolSelect(symbol, true)) {
      // Try with broker prefix/suffix variants
      Print("KaraBridge: Symbol not found: ", symbol);
      return BuildResponse(400, "{\\"error\\":\\"Symbol not found: " + symbol + "\\"}");
   }

   double ask = SymbolInfoDouble(symbol, SYMBOL_ASK);
   double bid = SymbolInfoDouble(symbol, SYMBOL_BID);
   bool   ok  = false;
   ulong  ticket = 0;

   if(action == "BUY") {
      ok = trade.Buy(volume, symbol, ask, sl, tp, "Kara");
      ticket = trade.ResultOrder();
   }
   else if(action == "SELL") {
      ok = trade.Sell(volume, symbol, bid, sl, tp, "Kara");
      ticket = trade.ResultOrder();
   }
   else if(action == "CLOSE") {
      for(int i = PositionsTotal() - 1; i >= 0; i--) {
         if(PositionGetSymbol(i) == symbol) {
            trade.PositionClose(PositionGetTicket(i));
         }
      }
      ok = true;
   }

   if(InpLogTrades) Print("KaraBridge: ", action, " ", symbol, " vol=", volume, " ok=", ok, " ticket=", ticket);

   string result = "{\\"status\\":\\"" + (ok ? "ok" : "error") + "\\",";
   result += "\\"ticket\\":" + IntegerToString(ticket) + ",";
   result += "\\"retcode\\":" + IntegerToString(trade.ResultRetcode()) + "}";
   return BuildResponse(ok ? 200 : 500, result);
}

//+------------------------------------------------------------------+
//| Parse string value from JSON                                     |
//+------------------------------------------------------------------+
string ParseJsonStr(string json, string key)
{
   string search = "\\"" + key + "\\":\\"";
   int pos = StringFind(json, search);
   if(pos < 0) return "";
   pos += StringLen(search);
   int end = StringFind(json, "\\"", pos);
   if(end < 0) return "";
   return StringSubstr(json, pos, end - pos);
}

//+------------------------------------------------------------------+
//| Parse double value from JSON                                     |
//+------------------------------------------------------------------+
double ParseJsonDbl(string json, string key, double def)
{
   string search = "\\"" + key + "\\":";
   int pos = StringFind(json, search);
   if(pos < 0) return def;
   pos += StringLen(search);
   int end = pos;
   while(end < StringLen(json)) {
      ushort c = StringGetCharacter(json, end);
      if(c == ',' || c == '}' || c == ' ') break;
      end++;
   }
   return StringToDouble(StringSubstr(json, pos, end - pos));
}`);
                  alert("✅ KaraBridge.mq5 source code copied to clipboard!");
                }}
                style={{
                  background: C.green + "22",
                  color: C.green,
                  border: `1px solid ${C.green}66`,
                  borderRadius: 6,
                  padding: "6px 14px",
                  fontSize: 13,
                  fontWeight: 700,
                  cursor: "pointer"
                }}
              >
                📋 Copy EA Code
              </button>
            </div>
            <pre style={{
              background: C.bg,
              border: `1px solid ${C.border}`,
              borderRadius: 8,
              padding: "12px",
              color: C.text,
              fontSize: 12,
              fontFamily: "monospace",
              maxHeight: 280,
              overflowY: "auto",
              whiteSpace: "pre-wrap"
            }}>
{`//+------------------------------------------------------------------+
//| KaraBridge.mq5 — Kara AI MT5 REST Bridge EA                      |
//+------------------------------------------------------------------+
input int    InpPort       = 8000;             // Local HTTP listen port
input string InpToken      = "kara_mt5_local"; // Auth token
input bool   InpLogTrades  = true;             // Log trades
input double InpMaxLot     = 1.0;              // Max lot safety cap

// 1. In MT5: Press F4 to open MetaEditor
// 2. Click New -> Expert Advisor (template) -> name it KaraBridge
// 3. Paste the full code, click F7 to compile (0 errors)
// 4. Drag KaraBridge onto any chart (e.g. EURUSD M1)
// 5. Check 'Allow Algo Trading' and click OK`}
            </pre>
          </Card>
        </>
      )}
    </div>
  );
}
